import { useEffect, useState, Fragment as ReactFragment } from "react";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import ImageUpload from "../components/ImageUpload";
import ReorderArrows from "../components/ReorderArrows";
import GiftTagPicker from "../components/GiftTagPicker";


// Metamark M4 Vinyl kleurenpalette — geschikt voor zowel Mat als Glas afwerking.
// Hex-waarden zijn dichtbenadering van de officiële Metamark M4 kleurenkaart.
const STICKER_COLORS = [
    // ----- Neutralen -----
    { name: "Wit",              hex: "#FFFFFF" },
    { name: "Zwart",            hex: "#1A1A1A" },
    { name: "Licht Grijs",      hex: "#C8C8C8" },
    { name: "Storm Grijs",      hex: "#6E7174" },
    { name: "Donker Grijs",     hex: "#4A4A4A" },
    { name: "Eierschaal",       hex: "#F0EAD6" },
    { name: "Amandel",          hex: "#D5C7AD" },
    // ----- Geel & oranje -----
    { name: "Citroen",          hex: "#FFF44F" },
    { name: "Midden Geel",      hex: "#FFCC00" },
    { name: "Boterbloem",       hex: "#F4B400" },
    { name: "Meloen",           hex: "#FFA858" },
    { name: "Oranje",           hex: "#FF7900" },
    // ----- Rood & roze -----
    { name: "Klaproos",         hex: "#E83F2D" },
    { name: "Kers",             hex: "#C8102E" },
    { name: "Karmijn",          hex: "#A6192E" },
    { name: "Vlammend Rood",    hex: "#D2362C" },
    { name: "Roze",             hex: "#F472B6" },
    { name: "Magenta",          hex: "#BF1B7E" },
    // ----- Groen -----
    { name: "Limoen",           hex: "#C4D600" },
    { name: "Klavergroen",      hex: "#009A44" },
    { name: "Midden Groen",     hex: "#00843D" },
    { name: "Bosgroen",         hex: "#154734" },
    { name: "Olijf",            hex: "#4A5D23" },
    { name: "Weide",            hex: "#7AB55C" },
    // ----- Blauw & teal -----
    { name: "Hemel",            hex: "#6CACE4" },
    { name: "Marina",           hex: "#006298" },
    { name: "Teal",             hex: "#00838F" },
    { name: "Kobalt",           hex: "#003DA5" },
    { name: "Ultramarijn",      hex: "#1F2A78" },
    { name: "Oceaan",           hex: "#0033A0" },
    // ----- Paars & metallics -----
    { name: "Paars",            hex: "#7B2CBF" },
    { name: "Bruin",            hex: "#6F4E37" },
    { name: "Zilver Metallic",  hex: "#C0C0C0" },
    { name: "Goud Metallic",    hex: "#D4AF37" },
];

// INTERN — Wholesale-doorklikken (B&C via Primex). Niet zichtbaar voor klanten; admin-only tab.
const PRIMEX_URLS = {
    "BC-E150":           "https://www.primex.nl/bc-e150-bc-5tu01t",
    "BC-E190":           "https://www.primex.nl/bc-e190-bc-5tu03t",
    "BC-EXACT190":       "https://www.primex.nl/bc-e220-bc-5tg002",
    "BC-EXACT150":       "https://www.primex.nl/bc-e220-oversized-bc-5tg001",
    "BC-INSPIRE-PLUS":   "https://www.primex.nl/bc-inspire-e150_-bc-5tu01b",
    "BC-WOMEN-150":      "https://www.primex.nl/bc-e150-women-bc-5tw02t",
    "BC-INSPIRE-HOODIE": "https://www.primex.nl/bc-inspire-bc-5wu33b",
    "BF638":             "https://www.primex.nl/beechfield-removable-patch-5-panel-cap-beechfield-bf638",
    "BF445R":            "https://www.primex.nl/beechfield-recycled-original-patch-beanie-beechfield-bf445r",
};

export default function AdminPage() {
    const { user, loading } = useAuth();
    const [tshirts, setTshirts] = useState([]);
    const [stickers, setStickers] = useState([]);
    const [prices, setPrices] = useState({
        design_fee: 4, sticker_default_price: 1.5, photo_print_fee: 6,
        etsen_wijnglas: 8, etsen_wijnglas_min: 6,
        etsen_whiskyglas: 12, etsen_whiskyglas_min: 4,
        etsen_stolp: 25, etsen_stolp_min: 1,
    });
    const [orders, setOrders] = useState([]);
    const [tab, setTab] = useState("dashboard");
    const [banner, setBanner] = useState({ banner_text: "", banner_active: true });
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [codes, setCodes] = useState([]);
    const [promoConfig, setPromoConfig] = useState({ volume_tiers: [], free_shipping_threshold: 75 });
    const [stats, setStats] = useState(null);
    const [inboxCounts, setInboxCounts] = useState({ messages_unhandled: 0, gallery_pending: 0 });
    const [accessories, setAccessories] = useState([]);
    const [supplierLinks, setSupplierLinks] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [giftCards, setGiftCards] = useState([]);
    const [giftProducts, setGiftProducts] = useState([]);
    const [shopProducts, setShopProducts] = useState([]);
    const [siteReviews, setSiteReviews] = useState([]);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        api.get("/tshirts").then(({ data }) => setTshirts(data));
        api.get("/stickers").then(({ data }) => setStickers(data));
        api.get("/prices").then(({ data }) => setPrices(data));
        api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
        api.get("/site/banner").then(({ data }) => setBanner(data)).catch(() => {});
        api.get("/admin/templates").then(({ data }) => setTemplates(data)).catch(() => {});
        api.get("/admin/discount-codes").then(({ data }) => setCodes(data)).catch(() => {});
        api.get("/promo/config").then(({ data }) => setPromoConfig(data)).catch(() => {});
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
        api.get("/admin/accessories").then(({ data }) => setAccessories(data || [])).catch(() => {});
        api.get("/admin/supplier-links").then(({ data }) => setSupplierLinks(data || [])).catch(() => {});
        api.get("/admin/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
        api.get("/admin/bundles").then(({ data }) => setBundles(data || [])).catch(() => {});
        api.get("/admin/gift-cards").then(({ data }) => setGiftCards(data || [])).catch(() => {});
        api.get("/admin/gift-products").then(({ data }) => setGiftProducts(data || [])).catch(() => {});
        api.get("/admin/shop-products").then(({ data }) => setShopProducts(data || [])).catch(() => {});
        api.get("/admin/site-reviews").then(({ data }) => setSiteReviews(data || [])).catch(() => {});

        const refreshInbox = () => api.get("/admin/inbox-counts").then(({ data }) => setInboxCounts(data)).catch(() => {});
        refreshInbox();
        const t = setInterval(refreshInbox, 30_000);   // poll elke 30s
        return () => clearInterval(t);
    }, [user]);

    if (loading) return <div className="p-10">…</div>;
    if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

    const updateTshirt = async (id, payload) => {
        const { data } = await api.patch(`/admin/tshirts/${id}`, payload);
        setTshirts(prev => prev.map(t => t.id === id ? data : t));
        toast.success(`Bijgewerkt: ${data.name}`);
    };

    const updateSticker = async (id, price) => {
        const { data } = await api.patch(`/admin/stickers/${id}`, { price: parseFloat(price) });
        setStickers(prev => prev.map(s => s.id === id ? data : s));
        toast.success("Sticker prijs bijgewerkt");
    };

    const savePrices = async () => {
        const { data } = await api.put("/admin/prices", {
            design_fee: parseFloat(prices.design_fee),
            sticker_default_price: parseFloat(prices.sticker_default_price),
            photo_print_fee: parseFloat(prices.photo_print_fee),
            etsen_wijnglas: parseFloat(prices.etsen_wijnglas),
            etsen_wijnglas_min: parseInt(prices.etsen_wijnglas_min) || 1,
            etsen_whiskyglas: parseFloat(prices.etsen_whiskyglas),
            etsen_whiskyglas_min: parseInt(prices.etsen_whiskyglas_min) || 1,
            etsen_stolp: parseFloat(prices.etsen_stolp),
            etsen_stolp_min: parseInt(prices.etsen_stolp_min) || 1,
        });
        setPrices(data);
        toast.success("Globale prijzen opgeslagen");
    };

    const saveBanner = async () => {
        try {
            const { data } = await api.put("/admin/site/banner", banner);
            setBanner(data);
            toast.success("Banner opgeslagen — direct zichtbaar op de homepage");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        }
    };

    return (
        <div data-testid="admin-page" className="bg-zinc-50 min-h-screen">
            <Toaster />
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                <p className="label-tiny">Admin</p>
                <h1 className="font-display text-5xl mt-2">Atelier Control Room.</h1>

                <div className="border border-zinc-200 bg-white mt-8 flex flex-wrap">
                    {["dashboard", "stickers", "accessoires", "tegels", "klanten", "giftcards", "reviews", "etsen", "supplier", "leveranciers", "promo", "banner", "messages", "orders"].map(t => {
                        const badge =
                            t === "messages" ? inboxCounts.messages_unhandled :
                            t === "gallery"  ? inboxCounts.gallery_pending :
                            0;
                        return (
                            <button data-testid={`admin-tab-${t}`} key={t} onClick={() => setTab(t)}
                                className={`relative px-5 py-3 text-xs font-bold uppercase tracking-widest border-r border-zinc-200 ${tab === t ? "bg-zinc-950 text-white" : ""}`}>
                                {t}
                                {badge > 0 && (
                                    <span
                                        data-testid={`admin-tab-${t}-badge`}
                                        className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold rounded-full px-1.5 ${tab === t ? "bg-rose-500 text-white" : "bg-rose-600 text-white"}`}>
                                        {badge > 99 ? "99+" : badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {tab === "tshirts" && (
                    <div className="mt-6 border border-zinc-200 bg-white">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-500">
                                <th className="p-4">SKU</th><th className="p-4">Naam</th><th className="p-4">GSM</th><th className="p-4">Prijs</th><th className="p-4">Acties</th>
                            </tr></thead>
                            <tbody>
                                {tshirts.map(t => (
                                    <TshirtRow key={t.id} t={t} onSave={updateTshirt} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "design" && (
                    <div className="mt-6 border border-zinc-200 bg-white p-6 max-w-lg space-y-4">
                        <p className="label-tiny">Globale ontwerpkosten</p>
                        <PriceField label="Ontwerpkost per tee (vast)" value={prices.design_fee} onChange={(v) => setPrices(p => ({ ...p, design_fee: v }))} testId="price-design-fee" />
                        <PriceField label="Standaard sticker prijs" value={prices.sticker_default_price} onChange={(v) => setPrices(p => ({ ...p, sticker_default_price: v }))} testId="price-sticker-default" />
                        <PriceField label="Foto print kost" value={prices.photo_print_fee} onChange={(v) => setPrices(p => ({ ...p, photo_print_fee: v }))} testId="price-photo-fee" />
                        <button onClick={savePrices} data-testid="admin-save-prices" className="btn-primary">Opslaan</button>
                    </div>
                )}

                {tab === "stickers" && (
                    <>
                        <StickerPricingAdmin />
                        <StickersAdmin
                            stickers={stickers}
                            onAdded={(s) => setStickers(prev => [...prev, s])}
                            onUpdated={(s) => setStickers(prev => prev.map(x => x.id === s.id ? s : x))}
                            onDeleted={(id) => setStickers(prev => prev.filter(x => x.id !== id))}
                        />
                    </>
                )}

                {tab === "templates" && (
                    <TemplatesAdmin
                        templates={templates}
                        tshirts={tshirts}
                        onAdded={(t) => setTemplates(prev => [t, ...prev])}
                        onUpdated={(t) => setTemplates(prev => prev.map(x => x.id === t.id ? t : x))}
                        onDeleted={(id) => setTemplates(prev => prev.filter(x => x.id !== id))}
                    />
                )}

                {tab === "accessoires" && (
                    <AccessoriesAdmin
                        accessories={accessories}
                        onAdded={(a) => setAccessories(prev => [...prev, a])}
                        onUpdated={(a) => setAccessories(prev => prev.map(x => x.id === a.id ? a : x))}
                        onDeleted={(id) => setAccessories(prev => prev.filter(x => x.id !== id))}
                    />
                )}

                {tab === "supplier" && (
                    <SupplierLinksAdmin links={supplierLinks} />
                )}

                {tab === "leveranciers" && (
                    <SuppliersAdmin
                        suppliers={suppliers}
                        onAdded={(s) => setSuppliers(prev => [...prev, s])}
                        onUpdated={(s) => setSuppliers(prev => prev.map(x => x.id === s.id ? s : x))}
                        onDeleted={(id) => setSuppliers(prev => prev.filter(x => x.id !== id))}
                    />
                )}

                {tab === "giftcards" && (
                    <GiftCardsAdmin cards={giftCards} onUpdated={(c) => setGiftCards(prev => prev.map(x => x.code === c.code ? c : x))} />
                )}

                {tab === "tegels" && (
                    <>
                        <TileSettingsAdmin />
                        <TileTemplatesAdmin />
                    </>
                )}

                {tab === "reviews" && (
                    <SiteReviewsAdmin
                        reviews={siteReviews}
                        onAdded={(r) => setSiteReviews(prev => [...prev, r])}
                        onUpdated={(r) => setSiteReviews(prev => prev.map(x => x.id === r.id ? r : x))}
                        onDeleted={(id) => setSiteReviews(prev => prev.filter(x => x.id !== id))}
                    />
                )}

                {tab === "dashboard" && (
                    <DashboardTab stats={stats} />
                )}

                {tab === "promo" && (
                    <PromoAdmin
                        codes={codes}
                        promoConfig={promoConfig}
                        onCodeAdded={(c) => setCodes(prev => [c, ...prev])}
                        onCodeDeleted={(id) => setCodes(prev => prev.filter(x => x.id !== id))}
                        onConfigSaved={setPromoConfig}
                    />
                )}

                {tab === "gallery" && (
                    <GalleryAdmin onChange={() => api.get("/admin/inbox-counts").then(({ data }) => setInboxCounts(data)).catch(() => {})} />
                )}

                {tab === "atelier" && (
                    <AtelierAdmin />
                )}

                {tab === "messages" && (
                    <MessagesAdmin onChange={() => api.get("/admin/inbox-counts").then(({ data }) => setInboxCounts(data)).catch(() => {})} />
                )}

                {tab === "workwear" && (
                    <WorkwearAdmin />
                )}


                {tab === "klanten" && (
                    <CustomersAdmin />
                )}

                {tab === "etsen" && (
                    <div data-testid="admin-etsen-section" className="mt-6 border border-zinc-200 bg-white p-6 space-y-5 max-w-2xl">
                        <div>
                            <p className="label-tiny">Etsen op glas — vanaf-prijzen</p>
                            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                                Deze prijzen verschijnen op de publieke <strong>/etsen-op-glas</strong> pagina als &laquo;vanaf-prijs per stuk&raquo;.
                                Je past ze hier op elk moment aan — de wijzigingen zijn direct zichtbaar op de site.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <PriceField label="Wijnglas — vanaf-prijs" value={prices.etsen_wijnglas}
                                onChange={(v) => setPrices(p => ({ ...p, etsen_wijnglas: v }))}
                                testId="price-etsen-wijnglas" />
                            <div>
                                <label className="label-tiny block mb-1">Minimum aantal wijnglazen</label>
                                <input type="number" min="1" data-testid="price-etsen-wijnglas-min" className="input-flat font-mono-dm"
                                    value={prices.etsen_wijnglas_min}
                                    onChange={(e) => setPrices(p => ({ ...p, etsen_wijnglas_min: e.target.value }))} />
                            </div>

                            <PriceField label="Whiskyglas — vanaf-prijs" value={prices.etsen_whiskyglas}
                                onChange={(v) => setPrices(p => ({ ...p, etsen_whiskyglas: v }))}
                                testId="price-etsen-whiskyglas" />
                            <div>
                                <label className="label-tiny block mb-1">Minimum aantal whiskyglazen</label>
                                <input type="number" min="1" data-testid="price-etsen-whiskyglas-min" className="input-flat font-mono-dm"
                                    value={prices.etsen_whiskyglas_min}
                                    onChange={(e) => setPrices(p => ({ ...p, etsen_whiskyglas_min: e.target.value }))} />
                            </div>

                            <PriceField label="Glazen stolp — vanaf-prijs" value={prices.etsen_stolp}
                                onChange={(v) => setPrices(p => ({ ...p, etsen_stolp: v }))}
                                testId="price-etsen-stolp" />
                            <div>
                                <label className="label-tiny block mb-1">Minimum aantal stolpen</label>
                                <input type="number" min="1" data-testid="price-etsen-stolp-min" className="input-flat font-mono-dm"
                                    value={prices.etsen_stolp_min}
                                    onChange={(e) => setPrices(p => ({ ...p, etsen_stolp_min: e.target.value }))} />
                            </div>
                        </div>

                        <div className="pt-4 border-t border-zinc-200">
                            <button onClick={savePrices} data-testid="admin-save-etsen-prices" className="btn-primary">
                                Etsen-prijzen opslaan
                            </button>
                            <p className="text-[11px] text-zinc-500 mt-2">
                                Tip: globale ontwerp- en stickerprijzen pas je aan in de Stickers tab.
                            </p>
                        </div>
                    </div>
                )}

                {tab === "banner" && (
                    <div data-testid="admin-banner-section" className="mt-6 border border-zinc-200 bg-white p-6 space-y-4">
                        <div>
                            <p className="label-tiny mb-2">Promo banner — bewegende tekst op de homepage</p>
                            <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                                Plaats je laatste nieuws, kortingen of nieuwe collecties hier. Gebruik <strong>★</strong> of <strong>•</strong> tussen items om ze visueel te scheiden.
                                Tip: kort en krachtig is het meest leesbaar in de scrolling banner.
                            </p>
                            <textarea
                                data-testid="admin-banner-text"
                                rows={4}
                                maxLength={500}
                                value={banner.banner_text}
                                onChange={(e) => setBanner(prev => ({ ...prev, banner_text: e.target.value }))}
                                placeholder="★ NIEUW: B&C Hoodies in stock ★ -10% op alle stickers t/m 31/03 ★ Gratis verzending vanaf €75 ★"
                                className="input-flat font-mono-dm text-sm"
                            />
                            <p className="text-[11px] text-zinc-400 mt-1 font-mono-dm">{banner.banner_text.length} / 500 tekens</p>
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                data-testid="admin-banner-active"
                                type="checkbox"
                                checked={banner.banner_active}
                                onChange={(e) => setBanner(prev => ({ ...prev, banner_active: e.target.checked }))}
                            />
                            <span>Banner zichtbaar op de homepage</span>
                        </label>
                        <div className="border-t border-zinc-200 pt-4">
                            <p className="label-tiny mb-2">Live voorbeeld</p>
                            <div className="bg-zinc-950 text-zinc-50 py-3 ticker overflow-hidden">
                                <div className="ticker-track text-sm font-mono-dm">
                                    {Array(2).fill(0).map((_, i) => (
                                        <span key={i}>{banner.banner_text || "(leeg)"}&nbsp;&nbsp;</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <button data-testid="admin-banner-save" onClick={saveBanner} className="btn-primary">
                            Banner opslaan
                        </button>
                    </div>
                )}

                {tab === "orders" && (
                    <div className="mt-6 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-xs text-zinc-500">{orders.length} bestelling{orders.length !== 1 ? "en" : ""} totaal</p>
                            <a
                                data-testid="orders-csv-export"
                                href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/orders.csv`}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                        const res = await api.get("/admin/orders.csv", { responseType: "blob" });
                                        const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = `bestellingen-${new Date().toISOString().slice(0,10)}.csv`;
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        window.URL.revokeObjectURL(url);
                                    } catch {
                                        toast.error("Export mislukt");
                                    }
                                }}
                                className="btn-primary text-xs inline-flex items-center gap-2">
                                ⬇ Exporteer als CSV (boekhouding)
                            </a>
                        </div>
                        <div className="border border-zinc-200 bg-white">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-500">
                                <th className="p-4">Bestelnr.</th><th className="p-4">Klant</th><th className="p-4">Items</th><th className="p-4">Betaling</th><th className="p-4">Totaal</th><th className="p-4">Status</th><th className="p-4">Acties</th>
                            </tr></thead>
                            <tbody>
                                {orders.map(o => (
                                    <ReactFragment key={o.id}>
                                    <tr className={`border-b border-zinc-100 ${o.status === "cancelled" ? "opacity-50 line-through" : ""}`}>
                                        <td className="p-4 font-mono-dm text-xs">
                                            <button
                                                data-testid={`admin-order-expand-${o.order_number}`}
                                                onClick={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)}
                                                className="hover:underline"
                                            >{expandedOrder === o.id ? "▼" : "▶"} {o.order_number}</button>
                                        </td>
                                        <td className="p-4">{o.user_email}</td>
                                        <td className="p-4">{o.items.length}</td>
                                        <td className="p-4">{o.payment_method}</td>
                                        <td className="p-4 font-mono-dm">€ {o.total.toFixed(2)}</td>
                                        <td className="p-4">
                                            <select
                                                data-testid={`admin-order-status-${o.order_number}`}
                                                value={o.status}
                                                onChange={async (e) => {
                                                    const { data } = await api.patch(`/admin/orders/${o.id}/status`, { status: e.target.value });
                                                    setOrders(prev => prev.map(x => x.id === o.id ? data : x));
                                                    toast.success(`Status: ${data.status}`);
                                                }}
                                                className="input-flat text-xs py-1"
                                            >
                                                <option value="pending_payment">In afwachting</option>
                                                <option value="paid">Betaald</option>
                                                <option value="printing">In productie</option>
                                                <option value="shipped">Verstuurd</option>
                                                <option value="cancelled">Geannuleerd</option>
                                            </select>
                                        </td>
                                        <td className="p-4 space-x-1">
                                            <button
                                                data-testid={`admin-notify-${o.order_number}`}
                                                onClick={async () => {
                                                    await api.post(`/admin/orders/${o.id}/notify`);
                                                    toast.success("Status mail verstuurd naar klant");
                                                }}
                                                className="text-xs text-emerald-700 hover:underline"
                                            >Mail status</button>
                                            <button
                                                data-testid={`admin-track-${o.order_number}`}
                                                onClick={async () => {
                                                    const tn = window.prompt("Bpost tracking nummer:", o.tracking_number || "");
                                                    if (tn === null) return;
                                                    try {
                                                        const { data } = await api.patch(`/admin/orders/${o.id}/tracking`, { tracking_number: tn });
                                                        setOrders(prev => prev.map(x => x.id === o.id ? data : x));
                                                        toast.success("Track & trace opgeslagen");
                                                    } catch (err) {
                                                        toast.error(err?.response?.data?.detail || "Mislukt");
                                                    }
                                                }}
                                                className="text-xs text-blue-700 hover:underline"
                                            >{o.tracking_number ? "✓ Track" : "Track"}</button>
                                            {o.status !== "cancelled" && (
                                                <button
                                                    data-testid={`admin-cancel-${o.order_number}`}
                                                    onClick={async () => {
                                                        if (!window.confirm(`Bestelling ${o.order_number} annuleren?`)) return;
                                                        const { data } = await api.patch(`/admin/orders/${o.id}/status`, { status: "cancelled" });
                                                        setOrders(prev => prev.map(x => x.id === o.id ? data : x));
                                                        toast.success("Geannuleerd");
                                                    }}
                                                    className="text-xs text-rose-600 hover:underline"
                                                >Annuleer</button>
                                            )}
                                            <button
                                                data-testid={`admin-delete-order-${o.order_number}`}
                                                onClick={async () => {
                                                    if (!window.confirm(`Bestelling ${o.order_number} definitief verwijderen?`)) return;
                                                    await api.delete(`/admin/orders/${o.id}`);
                                                    setOrders(prev => prev.filter(x => x.id !== o.id));
                                                    toast.success("Verwijderd");
                                                }}
                                                className="text-xs text-zinc-500 hover:text-rose-700 hover:underline"
                                            >Verwijder</button>
                                        </td>
                                    </tr>
                                    {expandedOrder === o.id && (
                                        <tr data-testid={`admin-order-details-${o.order_number}`} className="bg-amber-50/40 border-b border-zinc-200">
                                            <td colSpan={7} className="p-5">
                                                <p className="label-tiny mb-3 flex items-center gap-2">
                                                    <span className="bg-amber-200 text-amber-900 px-2 py-0.5 text-[9px] font-bold tracking-widest">INTERN</span>
                                                    Wholesale doorklikken — bestel rechtstreeks bij Primex
                                                </p>
                                                <div className="space-y-2">
                                                    {o.items.map((it, i) => {
                                                        const primexUrl = PRIMEX_URLS[it.tshirt_sku];
                                                        return (
                                                            <div key={i} className="flex items-center justify-between text-sm border border-zinc-200 bg-white p-3">
                                                                <div className="flex-1">
                                                                    <p className="font-semibold">{it.tshirt_name}
                                                                        <span className="ml-2 text-xs font-mono-dm text-zinc-500">{it.tshirt_sku}</span>
                                                                    </p>
                                                                    <p className="text-xs text-zinc-600 mt-0.5">
                                                                        Kleur: <strong>{it.tshirt_color_name}</strong> · Maat: <strong>{it.size}</strong> · Aantal: <strong>{it.quantity}×</strong>
                                                                    </p>
                                                                </div>
                                                                {primexUrl ? (
                                                                    <a
                                                                        href={primexUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        data-testid={`admin-primex-link-${o.order_number}-${i}`}
                                                                        className="ml-4 text-xs font-bold uppercase tracking-widest bg-zinc-950 text-white px-3 py-2 hover:bg-zinc-800"
                                                                    >→ Bestel bij Primex</a>
                                                                ) : (
                                                                    <span className="ml-4 text-xs text-zinc-400 italic">Geen Primex-link voor deze SKU</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <p className="text-[10px] text-zinc-500 mt-3 italic">Deze links zijn alleen zichtbaar voor admin-accounts. Klanten zien ze niet.</p>
                                            </td>
                                        </tr>
                                    )}
                                    </ReactFragment>
                                ))}
                                {orders.length === 0 && <tr><td className="p-6 text-zinc-500 text-center" colSpan={7}>Nog geen bestellingen.</td></tr>}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TshirtRow({ t, onSave }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: t.name, sku: t.sku, price: t.price, description: t.description,
        fabric: t.fabric, weight_gsm: t.weight_gsm, fit: t.fit, image_url: t.image_url,
        print_area_x: t.print_area?.x || 180,
        print_area_y: t.print_area?.y || 210,
        print_area_w: t.print_area?.w || 140,
        print_area_h: t.print_area?.h || 200,
    });
    const change = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
    const save = () => {
        const payload = {
            name: form.name, sku: form.sku, price: parseFloat(form.price),
            description: form.description, fabric: form.fabric,
            weight_gsm: parseInt(form.weight_gsm), fit: form.fit, image_url: form.image_url,
            print_area: {
                x: parseFloat(form.print_area_x), y: parseFloat(form.print_area_y),
                w: parseFloat(form.print_area_w), h: parseFloat(form.print_area_h),
            },
        };
        onSave(t.id, payload);
        setOpen(false);
    };
    return (
        <>
            <tr className="border-b border-zinc-100">
                <td className="p-4 font-mono-dm text-xs">{t.sku}</td>
                <td className="p-4">{t.name}</td>
                <td className="p-4">{t.weight_gsm}g</td>
                <td className="p-4">
                    <input data-testid={`admin-tshirt-price-${t.sku}`} type="number" step="0.10" value={form.price}
                        onChange={change("price")} className="input-flat w-24" />
                </td>
                <td className="p-4 flex gap-2">
                    <button data-testid={`admin-tshirt-save-${t.sku}`} onClick={save} className="btn-primary">Opslaan</button>
                    <button data-testid={`admin-tshirt-edit-${t.sku}`} onClick={() => setOpen(o => !o)} className="btn-ghost">{open ? "Sluit" : "Bewerk alles"}</button>
                </td>
            </tr>
            {open && (
                <tr className="bg-zinc-50">
                    <td colSpan={5} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Lbl k="Naam"><input className="input-flat" value={form.name} onChange={change("name")} /></Lbl>
                            <Lbl k="SKU"><input className="input-flat" value={form.sku} onChange={change("sku")} /></Lbl>
                            <Lbl k="Fit"><input className="input-flat" value={form.fit} onChange={change("fit")} /></Lbl>
                            <Lbl k="Stof"><input className="input-flat" value={form.fabric} onChange={change("fabric")} /></Lbl>
                            <Lbl k="Gewicht (GSM)"><input className="input-flat" type="number" value={form.weight_gsm} onChange={change("weight_gsm")} /></Lbl>
                            <Lbl k="Afbeelding URL"><input className="input-flat" value={form.image_url} onChange={change("image_url")} /></Lbl>
                            <div className="md:col-span-3">
                                <Lbl k="Beschrijving"><textarea rows={2} className="input-flat" value={form.description} onChange={change("description")} /></Lbl>
                            </div>
                            <Lbl k="Print zone X"><input className="input-flat" type="number" value={form.print_area_x} onChange={change("print_area_x")} /></Lbl>
                            <Lbl k="Print zone Y"><input className="input-flat" type="number" value={form.print_area_y} onChange={change("print_area_y")} /></Lbl>
                            <Lbl k="Print zone W × H">
                                <div className="flex gap-2">
                                    <input className="input-flat" type="number" value={form.print_area_w} onChange={change("print_area_w")} />
                                    <input className="input-flat" type="number" value={form.print_area_h} onChange={change("print_area_h")} />
                                </div>
                            </Lbl>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

function Lbl({ k, children }) {
    return <div><label className="label-tiny block mb-1.5">{k}</label>{children}</div>;
}

function PriceField({ label, value, onChange, testId }) {
    return (
        <div>
            <label className="label-tiny block mb-2">{label}</label>
            <input data-testid={testId} type="number" step="0.10" value={value} onChange={(e) => onChange(e.target.value)} className="input-flat" />
        </div>
    );
}

// ====== Stickers Admin — uitgebreid beheer (kleuren, prijs, voorbeeld, edit modal) ======
function StickersAdmin({ stickers, onAdded, onUpdated, onDeleted }) {
    const [search, setSearch] = useState("");
    const [filterCat, setFilterCat] = useState("all");
    const [editing, setEditing] = useState(null);   // sticker object
    const [adding, setAdding] = useState(false);

    const cats = ["general", "themes", "lineart", "letters", "numbers"];

    const filtered = stickers.filter(s => {
        const matchesCat = filterCat === "all" || s.category === filterCat;
        const matchesSearch = !search || (s.name || "").toLowerCase().includes(search.toLowerCase());
        return matchesCat && matchesSearch;
    });

    const removeSticker = async (s) => {
        if (!window.confirm(`Verwijder "${s.name}"?`)) return;
        try {
            await api.delete(`/admin/stickers/${s.id}`);
            onDeleted(s.id);
            toast.success("Sticker verwijderd");
        } catch {
            toast.error("Verwijderen mislukt");
        }
    };

    return (
        <div data-testid="stickers-admin-section" className="mt-6 space-y-4">
            {/* Toolbar */}
            <div className="border border-zinc-200 bg-white p-4 flex flex-wrap items-center gap-3">
                <button
                    data-testid="sticker-add-open"
                    onClick={() => setAdding(true)}
                    className="btn-primary text-xs inline-flex items-center gap-2">
                    + Nieuwe sticker
                </button>
                <input
                    data-testid="sticker-search"
                    type="search"
                    placeholder="Zoek op naam…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="input-flat max-w-xs"
                />
                <div className="flex flex-wrap gap-1.5 ml-auto">
                    <button
                        data-testid="sticker-filter-all"
                        onClick={() => setFilterCat("all")}
                        className={`text-[10px] px-2.5 py-1 border uppercase tracking-widest ${filterCat === "all" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`}>
                        Alles ({stickers.length})
                    </button>
                    {cats.map(c => {
                        const count = stickers.filter(s => s.category === c).length;
                        if (count === 0) return null;
                        return (
                            <button
                                key={c}
                                data-testid={`sticker-filter-${c}`}
                                onClick={() => setFilterCat(c)}
                                className={`text-[10px] px-2.5 py-1 border uppercase tracking-widest ${filterCat === c ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`}>
                                {c} ({count})
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
                <div className="border border-dashed border-zinc-300 bg-white p-12 text-center">
                    <p className="font-display text-2xl">Geen stickers gevonden</p>
                    <p className="text-sm text-zinc-500 mt-2">Pas filters aan of voeg een nieuwe sticker toe.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-0 border-l border-t border-zinc-200">
                    {filtered.map(s => (
                        <article
                            key={s.id}
                            data-testid={`admin-sticker-card-${s.id}`}
                            className="group border-r border-b border-zinc-200 bg-white p-4 flex flex-col">
                            {/* Preview */}
                            <button
                                onClick={() => setEditing(s)}
                                className="w-full aspect-square bg-zinc-50 border border-zinc-100 p-6 hover:border-zinc-950 transition-colors flex items-center justify-center"
                                data-testid={`sticker-preview-${s.id}`}>
                                <div className="w-full h-full" style={{ color: s.available_colors?.[0] || "#000" }} dangerouslySetInnerHTML={{ __html: s.svg }} />
                            </button>
                            {/* Info */}
                            <div className="mt-3 flex-1 flex flex-col">
                                <div className="flex items-baseline justify-between gap-2">
                                    <p className="font-semibold text-sm truncate">{s.name}</p>
                                    <p className="font-mono-dm text-xs whitespace-nowrap">€{(s.price || 0).toFixed(2)}</p>
                                </div>
                                <p className="label-tiny mt-1 text-zinc-400">{s.category}</p>

                                {/* Color chips */}
                                <div className="mt-3 min-h-[20px]">
                                    {s.available_colors && s.available_colors.length > 0 ? (
                                        <div className="flex flex-wrap gap-1" data-testid={`sticker-colors-${s.id}`}>
                                            {s.available_colors.slice(0, 8).map((hex, i) => (
                                                <span
                                                    key={i}
                                                    title={hex}
                                                    className="w-4 h-4 border border-zinc-300"
                                                    style={{ background: hex }}
                                                />
                                            ))}
                                            {s.available_colors.length > 8 && (
                                                <span className="text-[10px] text-zinc-500 self-center ml-1">+{s.available_colors.length - 8}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-[10px] text-amber-600 italic">⚠ Nog geen kleuren</span>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="mt-auto pt-3 flex gap-1.5 border-t border-zinc-100">
                                    <button
                                        data-testid={`sticker-edit-${s.id}`}
                                        onClick={() => setEditing(s)}
                                        className="flex-1 text-[10px] py-2 border border-zinc-300 hover:bg-zinc-950 hover:text-white hover:border-zinc-950 font-bold uppercase tracking-widest transition-colors">
                                        Bewerk
                                    </button>
                                    <button
                                        data-testid={`sticker-delete-${s.id}`}
                                        onClick={() => removeSticker(s)}
                                        className="text-[10px] py-2 px-2 text-rose-600 hover:bg-rose-50 font-bold uppercase tracking-widest">
                                        ✕
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {/* Modals */}
            {adding && (
                <StickerEditorModal
                    sticker={null}
                    onClose={() => setAdding(false)}
                    onSaved={(s) => { onAdded(s); setAdding(false); }}
                />
            )}
            {editing && (
                <StickerEditorModal
                    sticker={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(s) => { onUpdated(s); setEditing(null); }}
                />
            )}
        </div>
    );
}

function StickerEditorModal({ sticker, onClose, onSaved }) {
    const isNew = !sticker;
    const [form, setForm] = useState({
        name: sticker?.name || "",
        category: sticker?.category || "general",
        price: sticker?.price ?? 1.5,
        svg: sticker?.svg || "",
        image_url: sticker?.image_url || "",
        available_colors: sticker?.available_colors || [],
        for_sale: !!sticker?.for_sale,
        occasions: sticker?.occasions || [],
        recipients: sticker?.recipients || [],
        sale_sizes: sticker?.sale_sizes && sticker.sale_sizes.length > 0
            ? sticker.sale_sizes
            : [
                { label: "Klein",  width_mm: 50,  height_mm: 50,  price: 2.95 },
                { label: "Medium", width_mm: 100, height_mm: 100, price: 4.95 },
                { label: "Groot",  width_mm: 200, height_mm: 200, price: 9.95 },
            ],
    });
    const [previewColor, setPreviewColor] = useState(sticker?.available_colors?.[0] || "#000000");
    const [submitting, setSubmitting] = useState(false);

    const updateSize = (idx, field, value) => {
        setForm(f => ({
            ...f,
            sale_sizes: f.sale_sizes.map((s, i) =>
                i === idx ? { ...s, [field]: field === "label" ? value : (parseFloat(value) || 0) } : s
            ),
        }));
    };
    const addSize = () => {
        setForm(f => ({
            ...f,
            sale_sizes: [...f.sale_sizes, { label: "Nieuw", width_mm: 50, height_mm: 50, price: 1.95 }],
        }));
    };
    const removeSize = (idx) => {
        setForm(f => ({ ...f, sale_sizes: f.sale_sizes.filter((_, i) => i !== idx) }));
    };

    const toggleColor = (hex) => {
        setForm(f => ({
            ...f,
            available_colors: f.available_colors.includes(hex)
                ? f.available_colors.filter(c => c !== hex)
                : [...f.available_colors, hex],
        }));
    };

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type === "image/svg+xml" || file.name.endsWith(".svg")) {
            const reader = new FileReader();
            reader.onload = () => setForm(f => ({ ...f, svg: String(reader.result) }));
            reader.readAsText(file);
        } else {
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = String(reader.result);
                setForm(f => ({ ...f, svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><image href="${dataUrl}" width="64" height="64" preserveAspectRatio="xMidYMid meet"/></svg>` }));
            };
            reader.readAsDataURL(file);
        }
        if (!form.name) setForm(f => ({ ...f, name: file.name.replace(/\.[^.]+$/, "") }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Naam is verplicht"); return; }
        if (!form.svg) { toast.error("Bestand is verplicht"); return; }
        if (form.available_colors.length === 0) {
            if (!window.confirm("Geen kleuren geselecteerd — klanten zien deze sticker in zijn originele kleur. Doorgaan?")) return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                category: form.category,
                price: parseFloat(form.price) || 0,
                svg: form.svg,
                image_url: (form.image_url || "").trim(),
                available_colors: form.available_colors,
                for_sale: form.for_sale,
                sale_sizes: form.for_sale ? form.sale_sizes.filter(s => s.label && s.width_mm > 0 && s.height_mm > 0) : [],
                occasions: form.occasions || [],
                recipients: form.recipients || [],
            };
            const { data } = isNew
                ? await api.post("/admin/stickers", payload)
                : await api.patch(`/admin/stickers/${sticker.id}`, payload);
            onSaved(data);
            toast.success(isNew ? "Sticker toegevoegd" : "Sticker opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div onClick={onClose} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in overflow-y-auto">
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                data-testid="sticker-editor-modal"
                className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto">

                <div className="p-5 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <p className="label-tiny">{isNew ? "Nieuw" : "Bewerken"}</p>
                        <p className="font-display text-2xl mt-1">{isNew ? "Nieuwe sticker" : form.name || "Sticker"}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-950" data-testid="sticker-modal-close">✕</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0">
                    {/* Preview kolom */}
                    <div className="p-6 border-r border-zinc-100 bg-zinc-50/60 space-y-4">
                        <p className="label-tiny">Voorbeeld</p>
                        <div className="aspect-square bg-white border border-zinc-200 p-8 flex items-center justify-center">
                            {form.svg ? (
                                <div
                                    className="w-full h-full"
                                    style={{ color: previewColor }}
                                    dangerouslySetInnerHTML={{ __html: form.svg }}
                                />
                            ) : (
                                <p className="text-xs text-zinc-400 text-center">Upload een SVG of PNG hieronder</p>
                            )}
                        </div>
                        {form.available_colors.length > 0 && (
                            <div>
                                <p className="label-tiny mb-2">Test andere kleur</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {form.available_colors.map(hex => (
                                        <button
                                            key={hex}
                                            type="button"
                                            data-testid={`sticker-preview-color-${hex}`}
                                            onClick={() => setPreviewColor(hex)}
                                            title={hex}
                                            className={`w-6 h-6 border-2 transition-transform hover:scale-110 ${previewColor === hex ? "border-zinc-950" : "border-zinc-200"}`}
                                            style={{ background: hex }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        <label className="block">
                            <span className="label-tiny">Upload bestand (SVG/PNG)</span>
                            <input
                                data-testid="sticker-modal-file"
                                type="file"
                                accept="image/svg+xml,image/png,image/jpeg"
                                onChange={onFile}
                                className="mt-2 text-xs file:btn-primary file:border-0 file:cursor-pointer"
                            />
                            <p className="text-[10px] text-zinc-400 mt-1.5 leading-snug">
                                💡 Voor beste resultaten gebruik SVG met <code className="bg-zinc-100 px-1">fill=&quot;currentColor&quot;</code> — dan kleurt de sticker automatisch.
                            </p>
                        </label>
                    </div>

                    {/* Velden kolom */}
                    <div className="p-6 space-y-5">
                        <div>
                            <label className="label-tiny block mb-2">Naam *</label>
                            <input
                                data-testid="sticker-modal-name"
                                required
                                type="text"
                                maxLength={80}
                                value={form.name}
                                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Bijv. Voetbal — heart"
                                className="input-flat"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-tiny block mb-2">Categorie *</label>
                                <select
                                    data-testid="sticker-modal-category"
                                    value={form.category}
                                    onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                                    className="input-flat">
                                    <option value="general">general</option>
                                    <option value="themes">themes</option>
                                    <option value="lineart">lineart</option>
                                    <option value="letters">letters</option>
                                    <option value="numbers">numbers</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Prijs (€) *</label>
                                <input
                                    data-testid="sticker-modal-price"
                                    required
                                    type="number"
                                    step="0.10"
                                    min="0"
                                    value={form.price}
                                    onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                                    className="input-flat"
                                />
                            </div>
                        </div>

                        {/* Beschikbare kleuren */}
                        <div>
                            <div className="flex items-baseline justify-between mb-2">
                                <label className="label-tiny">Beschikbare kleuren ({form.available_colors.length}/{STICKER_COLORS.length})</label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        data-testid="sticker-colors-select-all"
                                        onClick={() => setForm(f => ({ ...f, available_colors: STICKER_COLORS.map(c => c.hex) }))}
                                        className="text-[10px] text-rose-600 hover:underline">
                                        Alles
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="sticker-colors-clear"
                                        onClick={() => setForm(f => ({ ...f, available_colors: [] }))}
                                        className="text-[10px] text-zinc-400 hover:underline">
                                        Wis
                                    </button>
                                </div>
                            </div>
                            <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                                Klik om in/uit te schakelen. Klanten kunnen enkel kiezen uit de aangevinkte kleuren.
                            </p>
                            <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                                {STICKER_COLORS.map(c => {
                                    const active = form.available_colors.includes(c.hex);
                                    return (
                                        <button
                                            key={c.hex}
                                            type="button"
                                            data-testid={`sticker-color-${c.name}`}
                                            onClick={() => toggleColor(c.hex)}
                                            title={c.name}
                                            className={`group relative aspect-square border-2 transition-all hover:scale-105 ${active ? "border-zinc-950 ring-2 ring-zinc-950 ring-offset-2" : "border-zinc-200 hover:border-zinc-400"}`}
                                            style={{ background: c.hex }}>
                                            {active && (
                                                <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg" style={{ textShadow: "0 0 4px rgba(0,0,0,0.7)" }}>
                                                    ✓
                                                </span>
                                            )}
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 text-[9px] text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">{c.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* For sale toggle */}
                        <div className="border-t border-zinc-100 pt-4">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    data-testid="sticker-modal-for-sale"
                                    type="checkbox"
                                    checked={form.for_sale}
                                    onChange={(e) => setForm(f => ({ ...f, for_sale: e.target.checked }))}
                                    className="mt-1 w-4 h-4 accent-rose-600"
                                />
                                <div className="flex-1">
                                    <p className="font-semibold text-sm">Verkrijgbaar als losse sticker</p>
                                    <p className="text-xs text-zinc-500 mt-0.5">Toon deze sticker op de <code>/stickers</code> winkelpagina als product (met afmetingen).</p>
                                </div>
                            </label>
                        </div>

                        {/* Product foto (optioneel) — voor de webshopkaart */}
                        <div className="border-t border-zinc-100 pt-4">
                            <label className="label-tiny block mb-2">Product foto (optioneel)</label>
                            <p className="text-xs text-zinc-500 mb-2">
                                Toon een echte foto van de sticker (bv. op product, gedrukt, gestoken) op de webshopkaart.
                                Laat leeg om het SVG-symbool hierboven te gebruiken.
                            </p>
                            <ImageUpload
                                value={form.image_url}
                                onChange={(url) => setForm(f => ({ ...f, image_url: url }))}
                                testId="sticker-modal-image"
                            />
                        </div>

                        {/* Cadeau-vinder tags */}
                        <div className="border-t border-zinc-100 pt-4">
                            <p className="label-tiny mb-2 text-rose-600">★ Cadeau-vinder tags (optioneel)</p>
                            <p className="text-xs text-zinc-500 mb-2">
                                Tag deze sticker met gelegenheden/ontvangers — hij verschijnt dan ook in de Cadeau-vinder en quiz.
                            </p>
                            <GiftTagPicker
                                occasions={form.occasions || []}
                                recipients={form.recipients || []}
                                onChange={({ occasions, recipients }) => setForm(f => ({ ...f, occasions, recipients }))}
                            />
                        </div>

                        {/* Afmetingen & prijs per maat (alleen als for_sale aan staat) */}
                        {form.for_sale && (
                            <div data-testid="sticker-sizes-section" className="border-t border-zinc-100 pt-4">
                                <div className="flex items-baseline justify-between mb-2">
                                    <label className="label-tiny">Afmetingen &amp; prijs per maat *</label>
                                    <button
                                        type="button"
                                        data-testid="sticker-size-add"
                                        onClick={addSize}
                                        className="text-[10px] font-bold uppercase tracking-widest text-rose-600 hover:underline">
                                        + Maat toevoegen
                                    </button>
                                </div>
                                <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                                    Klant kiest één van deze maten bij aankoop. Afmetingen zijn in millimeter (mm).
                                </p>
                                <div className="space-y-2">
                                    <div className="grid grid-cols-[1fr_70px_70px_90px_30px] gap-2 px-2 text-[10px] uppercase tracking-widest text-zinc-500">
                                        <span>Naam</span>
                                        <span>Breedte (mm)</span>
                                        <span>Hoogte (mm)</span>
                                        <span>Prijs (€)</span>
                                        <span></span>
                                    </div>
                                    {form.sale_sizes.length === 0 && (
                                        <p className="text-xs text-amber-700 italic px-2">Voeg minstens 1 maat toe.</p>
                                    )}
                                    {form.sale_sizes.map((s, idx) => (
                                        <div key={idx} className="grid grid-cols-[1fr_70px_70px_90px_30px] gap-2 items-center">
                                            <input
                                                data-testid={`sticker-size-label-${idx}`}
                                                type="text"
                                                value={s.label}
                                                onChange={(e) => updateSize(idx, "label", e.target.value)}
                                                placeholder="bv. Klein"
                                                className="px-2 py-1.5 border border-zinc-300 text-xs focus:outline-none focus:border-zinc-950"
                                            />
                                            <input
                                                data-testid={`sticker-size-width-${idx}`}
                                                type="number" min="10" max="2000"
                                                value={s.width_mm}
                                                onChange={(e) => updateSize(idx, "width_mm", e.target.value)}
                                                className="px-2 py-1.5 border border-zinc-300 text-xs font-mono-dm focus:outline-none focus:border-zinc-950"
                                            />
                                            <input
                                                data-testid={`sticker-size-height-${idx}`}
                                                type="number" min="10" max="2000"
                                                value={s.height_mm}
                                                onChange={(e) => updateSize(idx, "height_mm", e.target.value)}
                                                className="px-2 py-1.5 border border-zinc-300 text-xs font-mono-dm focus:outline-none focus:border-zinc-950"
                                            />
                                            <input
                                                data-testid={`sticker-size-price-${idx}`}
                                                type="number" min="0" step="0.10"
                                                value={s.price}
                                                onChange={(e) => updateSize(idx, "price", e.target.value)}
                                                className="px-2 py-1.5 border border-zinc-300 text-xs font-mono-dm focus:outline-none focus:border-zinc-950"
                                            />
                                            <button
                                                type="button"
                                                data-testid={`sticker-size-remove-${idx}`}
                                                onClick={() => removeSize(idx)}
                                                className="text-zinc-400 hover:text-rose-600 text-lg"
                                                title="Verwijder maat">×</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Submit row */}
                        <div className="border-t border-zinc-100 pt-4 flex flex-wrap justify-end gap-2">
                            <button type="button" onClick={onClose} className="btn-ghost">Annuleer</button>
                            <button type="submit" disabled={submitting} data-testid="sticker-modal-save" className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                                {submitting ? "Bezig…" : (isNew ? "Sticker toevoegen" : "Wijzigingen opslaan")}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

// ====== Templates Admin (vaste designs voor T-shirts en hoodies) ======
function TemplatesAdmin({ templates, tshirts, onAdded, onUpdated, onDeleted }) {
    const [form, setForm] = useState({
        name: "",
        description: "",
        garment_type: "tshirt",
        garment_sku: "",
        preview_image_url: "",
        price: 24.95,
        default_color_hex: "#FFFFFF",
        default_color_name: "White",
        available_sizes: ["S", "M", "L", "XL"],
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);

    const setF = (k) => (e) => {
        const v = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [k]: v }));
    };
    const toggleSize = (s) => setForm(prev => ({
        ...prev,
        available_sizes: prev.available_sizes.includes(s)
            ? prev.available_sizes.filter(x => x !== s)
            : [...prev.available_sizes, s]
    }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.garment_sku || !form.preview_image_url) {
            toast.error("Naam, model en preview afbeelding zijn verplicht");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/templates", { ...form, price: parseFloat(form.price) });
            onAdded(data);
            toast.success("Template toegevoegd");
            setForm(prev => ({ ...prev, name: "", description: "", preview_image_url: "", price: 24.95 }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setSubmitting(false);
        }
    };

    const allSizes = ["XS","S","M","L","XL","XXL","3XL"];

    return (
        <div className="mt-6 space-y-6">
            <form onSubmit={onSubmit} data-testid="admin-add-template-form" className="border border-zinc-200 bg-white p-6 space-y-4">
                <p className="label-tiny">Nieuw vast ontwerp toevoegen</p>
                <p className="text-xs text-zinc-500 leading-relaxed">Vaste designs zijn kant-en-klare ontwerpen die klanten direct kunnen kopen, zonder zelf te ontwerpen. Plaats hier de naam, kies het type kledingstuk en model, voeg een productfoto (URL) toe en stel de prijs in.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Naam"><input data-testid="template-name" required value={form.name} onChange={setF("name")} className="input-flat" /></Field>
                    <Field label="Type">
                        <select data-testid="template-garment-type" value={form.garment_type} onChange={setF("garment_type")} className="input-flat">
                            <option value="tshirt">T-shirt</option>
                            <option value="hoodie">Hoodie</option>
                        </select>
                    </Field>
                    <Field label="Basis model">
                        <select data-testid="template-garment-sku" value={form.garment_sku} onChange={setF("garment_sku")} required className="input-flat">
                            <option value="">— Kies —</option>
                            {tshirts.filter(t => (form.garment_type === "hoodie" ? t.garment_type === "hoodie" : (t.garment_type === "tshirt" || !t.garment_type))).map(t => (
                                <option key={t.sku} value={t.sku}>{t.name} ({t.sku})</option>
                            ))}
                        </select>
                    </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Preview afbeelding URL" wide><input data-testid="template-image" required placeholder="https://…" value={form.preview_image_url} onChange={setF("preview_image_url")} className="input-flat" /></Field>
                    <Field label="Prijs (€)"><input data-testid="template-price" type="number" step="0.50" required value={form.price} onChange={setF("price")} className="input-flat" /></Field>
                </div>
                <Field label="Korte beschrijving"><input value={form.description} onChange={setF("description")} placeholder="bv. Limited edition zomer collectie" className="input-flat" /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Standaard kleur (hex)"><input value={form.default_color_hex} onChange={setF("default_color_hex")} className="input-flat font-mono-dm" /></Field>
                    <Field label="Standaard kleur naam"><input value={form.default_color_name} onChange={setF("default_color_name")} className="input-flat" /></Field>
                </div>
                <div>
                    <p className="label-tiny mb-2">Beschikbare maten</p>
                    <div className="flex flex-wrap gap-2">
                        {allSizes.map(s => (
                            <button type="button" key={s} data-testid={`template-size-${s}`} onClick={() => toggleSize(s)}
                                className={`px-3 py-1.5 text-xs font-bold border ${form.available_sizes.includes(s) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200"}`}>{s}</button>
                        ))}
                    </div>
                </div>
                {form.preview_image_url && (
                    <div className="border border-zinc-200 p-3 inline-block bg-zinc-50">
                        <img src={form.preview_image_url} alt="preview" className="w-32 h-32 object-contain" />
                    </div>
                )}
                <button type="submit" disabled={submitting} data-testid="template-submit" className="btn-primary">
                    {submitting ? "Bezig…" : "Voeg vast ontwerp toe"}
                </button>
            </form>

            <div className="border border-zinc-200 bg-white">
                <p className="label-tiny p-4 border-b border-zinc-200">{templates.length} vaste ontwerp{templates.length === 1 ? "" : "en"}</p>
                {templates.length === 0 && (
                    <p className="p-6 text-sm text-zinc-500 text-center">Nog geen vaste designs. Voeg er eentje toe via het formulier hierboven.</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-zinc-200">
                    {templates.map(t => (
                        <div key={t.id} data-testid={`admin-template-row-${t.id}`} className="border-r border-b border-zinc-200 p-4">
                            <div className="aspect-square bg-zinc-50 border border-zinc-200 overflow-hidden mb-3">
                                <img src={t.preview_image_url} alt={t.name} className="w-full h-full object-contain" />
                            </div>
                            <p className="font-semibold text-sm">{t.name}</p>
                            <p className="text-[11px] text-zinc-500 font-mono-dm">{t.garment_type} · {t.garment_sku} · {t.available_sizes.join(",")}</p>
                            <div className="flex items-center justify-between mt-2">
                                <input type="number" step="0.50" defaultValue={t.price}
                                    onBlur={async (e) => {
                                        const newPrice = parseFloat(e.target.value);
                                        if (newPrice === t.price) return;
                                        const { data } = await api.patch(`/admin/templates/${t.id}`, { price: newPrice });
                                        onUpdated(data);
                                        toast.success("Prijs bijgewerkt");
                                    }}
                                    className="input-flat text-xs w-24" />
                                <label className="text-xs flex items-center gap-1">
                                    <input type="checkbox" checked={t.active} onChange={async (e) => {
                                        const { data } = await api.patch(`/admin/templates/${t.id}`, { active: e.target.checked });
                                        onUpdated(data);
                                    }} />
                                    Actief
                                </label>
                            </div>
                            <button data-testid={`admin-template-delete-${t.id}`} onClick={async () => {
                                if (!window.confirm(`Verwijder "${t.name}"?`)) return;
                                await api.delete(`/admin/templates/${t.id}`);
                                onDeleted(t.id);
                                toast.success("Verwijderd");
                            }} className="text-[10px] text-rose-600 hover:underline mt-2">Verwijder</button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, wide }) {
    return (
        <label className={`block ${wide ? "md:col-span-2" : ""}`}>
            <span className="label-tiny block mb-1">{label}</span>
            {children}
        </label>
    );
}

// ====== Promo Admin (kortingscodes + volume korting + gratis-verzending drempel) ======
function PromoAdmin({ codes, promoConfig, onCodeAdded, onCodeDeleted, onConfigSaved }) {
    const [newCode, setNewCode] = useState({
        code: "", discount_type: "percent", value: 10, min_order_total: 0,
        free_shipping: false, active: true, max_uses: "", expires_at: "",
    });
    const [cfg, setCfg] = useState({
        free_shipping_threshold: promoConfig.free_shipping_threshold || 75,
        volume_tiers: promoConfig.volume_tiers || [
            { min_qty: 3, percent: 5 },
            { min_qty: 5, percent: 10 },
            { min_qty: 10, percent: 15 },
        ],
    });

    useEffect(() => {
        setCfg({
            free_shipping_threshold: promoConfig.free_shipping_threshold || 75,
            volume_tiers: promoConfig.volume_tiers || cfg.volume_tiers,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promoConfig]);

    const setN = (k) => (e) => {
        const v = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setNewCode(prev => ({ ...prev, [k]: v }));
    };

    const addCode = async (e) => {
        e.preventDefault();
        if (!newCode.code.trim()) return toast.error("Vul een code in");
        try {
            const payload = {
                ...newCode,
                value: parseFloat(newCode.value),
                min_order_total: parseFloat(newCode.min_order_total) || 0,
                max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
                expires_at: newCode.expires_at || null,
            };
            const { data } = await api.post("/admin/discount-codes", payload);
            onCodeAdded(data);
            toast.success(`Code "${data.code}" aangemaakt`);
            setNewCode(prev => ({ ...prev, code: "", value: 10, min_order_total: 0 }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
        }
    };

    const saveConfig = async () => {
        try {
            const { data } = await api.put("/admin/promo/config", {
                free_shipping_threshold: parseFloat(cfg.free_shipping_threshold),
                volume_tiers: cfg.volume_tiers.map(t => ({ min_qty: parseInt(t.min_qty), percent: parseFloat(t.percent) })),
            });
            onConfigSaved(data);
            toast.success("Promotie instellingen opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        }
    };

    const updateTier = (i, k, v) => setCfg(prev => ({
        ...prev,
        volume_tiers: prev.volume_tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t)
    }));

    return (
        <div className="mt-6 space-y-8">
            {/* Volume korting + gratis verzending instellingen */}
            <div className="border border-zinc-200 bg-white p-6 space-y-4" data-testid="admin-promo-config">
                <p className="label-tiny">Algemene promoties</p>
                <p className="text-xs text-zinc-500 leading-relaxed">
                    Volume korting werkt automatisch zodra de klant meer items in zijn mandje heeft. Gratis verzending vanaf het ingestelde bedrag wordt ook automatisch toegepast.
                </p>
                <div>
                    <label className="label-tiny block mb-1">Gratis verzending vanaf (€)</label>
                    <input data-testid="promo-free-ship-threshold" type="number" step="5" className="input-flat max-w-[140px]"
                        value={cfg.free_shipping_threshold}
                        onChange={(e) => setCfg(prev => ({ ...prev, free_shipping_threshold: e.target.value }))} />
                </div>
                <div>
                    <p className="label-tiny mb-2">Volume korting (3 stappen)</p>
                    {cfg.volume_tiers.map((t, i) => (
                        <div key={i} className="flex items-center gap-3 mb-2">
                            <span className="text-xs text-zinc-500 w-24">vanaf</span>
                            <input data-testid={`promo-tier-${i}-qty`} type="number" min={1} className="input-flat w-20" value={t.min_qty}
                                onChange={(e) => updateTier(i, "min_qty", e.target.value)} />
                            <span className="text-xs text-zinc-500">items =</span>
                            <input data-testid={`promo-tier-${i}-percent`} type="number" min={0} max={50} step="0.5" className="input-flat w-20" value={t.percent}
                                onChange={(e) => updateTier(i, "percent", e.target.value)} />
                            <span className="text-xs text-zinc-500">% korting</span>
                        </div>
                    ))}
                </div>
                <button data-testid="promo-config-save" onClick={saveConfig} className="btn-primary">Opslaan</button>
            </div>

            {/* Kortingscode aanmaken */}
            <form onSubmit={addCode} data-testid="admin-add-code-form" className="border border-zinc-200 bg-white p-6 space-y-4">
                <p className="label-tiny">Nieuwe kortingscode</p>
                <p className="text-xs text-zinc-500 leading-relaxed">Klanten typen deze code in op de checkout pagina. Tip: kondig de code aan via de bewegende banner bovenaan de homepage.</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <label className="block"><span className="label-tiny block mb-1">Code</span>
                        <input data-testid="code-input-code" required value={newCode.code}
                            onChange={(e) => setNewCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                            placeholder="ZOMER15" className="input-flat font-mono-dm uppercase" />
                    </label>
                    <label className="block"><span className="label-tiny block mb-1">Type</span>
                        <select data-testid="code-input-type" value={newCode.discount_type} onChange={setN("discount_type")} className="input-flat">
                            <option value="percent">% korting</option>
                            <option value="fixed">€ vast bedrag</option>
                        </select>
                    </label>
                    <label className="block"><span className="label-tiny block mb-1">Waarde</span>
                        <input data-testid="code-input-value" type="number" min={0} step="0.5" required value={newCode.value} onChange={setN("value")} className="input-flat" />
                    </label>
                    <label className="block"><span className="label-tiny block mb-1">Min. orderbedrag (€)</span>
                        <input data-testid="code-input-min" type="number" min={0} step="5" value={newCode.min_order_total} onChange={setN("min_order_total")} className="input-flat" />
                    </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="block"><span className="label-tiny block mb-1">Max. aantal gebruiken (leeg = ∞)</span>
                        <input data-testid="code-input-maxuses" type="number" min={1} value={newCode.max_uses} onChange={setN("max_uses")} className="input-flat" />
                    </label>
                    <label className="block"><span className="label-tiny block mb-1">Vervaldatum (optioneel)</span>
                        <input data-testid="code-input-expires" type="date" value={newCode.expires_at} onChange={setN("expires_at")} className="input-flat" />
                    </label>
                    <label className="flex items-center gap-2 mt-6 text-sm">
                        <input data-testid="code-input-freeship" type="checkbox" checked={newCode.free_shipping} onChange={setN("free_shipping")} />
                        + Gratis verzending
                    </label>
                </div>
                <button type="submit" data-testid="code-submit" className="btn-primary">Code aanmaken</button>
            </form>

            {/* Codes overzicht */}
            <div className="border border-zinc-200 bg-white">
                <p className="label-tiny p-4 border-b border-zinc-200">{codes.length} kortingscode{codes.length === 1 ? "" : "s"}</p>
                {codes.length === 0 ? (
                    <p className="p-6 text-sm text-zinc-500 text-center">Nog geen kortingscodes. Maak er eentje aan hierboven.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                            <tr><th className="p-3">Code</th><th>Waarde</th><th>Min</th><th>Gebruikt</th><th>Verloopt</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                            {codes.map(c => (
                                <tr key={c.id} className="border-b border-zinc-100" data-testid={`admin-code-row-${c.code}`}>
                                    <td className="p-3 font-mono-dm font-bold">{c.code}{c.free_shipping && <span className="ml-2 text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5">+ FREE SHIP</span>}</td>
                                    <td>{c.discount_type === "percent" ? `${c.value}%` : `€${c.value.toFixed(2)}`}</td>
                                    <td className="text-zinc-500">€{(c.min_order_total || 0).toFixed(2)}</td>
                                    <td className="text-zinc-500">{c.times_used}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                                    <td className="text-zinc-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                                    <td>{c.active ? <span className="text-emerald-700">Actief</span> : <span className="text-zinc-400">Uit</span>}</td>
                                    <td className="text-right pr-3">
                                        <button data-testid={`admin-code-delete-${c.code}`} onClick={async () => {
                                            if (!window.confirm(`Verwijder code ${c.code}?`)) return;
                                            await api.delete(`/admin/discount-codes/${c.id}`);
                                            onCodeDeleted(c.id);
                                            toast.success("Verwijderd");
                                        }} className="text-xs text-rose-600 hover:underline">Verwijder</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}


// ====== Dashboard Tab (admin cijfers + overzicht) ======
function DashboardTab({ stats }) {
    if (!stats) return <p className="mt-10 text-zinc-500">Cijfers laden…</p>;
    const cards = [
        { label: "Omzet deze maand", value: `€ ${stats.revenue_month.toFixed(2)}`, sub: `${stats.orders_this_month} bestelling${stats.orders_this_month === 1 ? "" : "en"}` },
        { label: "Omzet dit jaar", value: `€ ${stats.revenue_year.toFixed(2)}`, sub: `${stats.orders_this_year} bestelling${stats.orders_this_year === 1 ? "" : "en"}` },
        { label: "Totale omzet", value: `€ ${stats.revenue_total.toFixed(2)}`, sub: `${stats.total_orders} bestelling${stats.total_orders === 1 ? "" : "en"} ooit` },
        { label: "Gem. orderbedrag", value: `€ ${stats.avg_order.toFixed(2)}`, sub: "per bestelling" },
        { label: "Klanten", value: stats.total_customers, sub: "geregistreerd" },
        { label: "Actieve codes", value: stats.active_codes, sub: `${stats.active_templates} vaste designs` },
    ];
    return (
        <div className="mt-6 space-y-6" data-testid="admin-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-l border-t border-zinc-200">
                {cards.map(c => (
                    <div key={c.label} className="border-r border-b border-zinc-200 bg-white p-6">
                        <p className="label-tiny">{c.label}</p>
                        <p className="font-display text-3xl mt-2">{c.value}</p>
                        <p className="text-xs text-zinc-500 mt-1">{c.sub}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-zinc-200">
                {/* Top SKUs */}
                <div className="border-r border-b border-zinc-200 bg-white p-6">
                    <p className="label-tiny mb-4">Top 5 producten (op aantal verkocht)</p>
                    {stats.top_skus.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nog geen verkochte items.</p>
                    ) : (
                        <ul className="space-y-2">
                            {stats.top_skus.map((s, i) => (
                                <li key={s.sku} className="flex items-center gap-3 text-sm">
                                    <span className="font-mono-dm text-xs w-6 text-zinc-400">#{i + 1}</span>
                                    <span className="flex-1 font-mono-dm text-xs">{s.sku}</span>
                                    <span className="font-bold">{s.qty}×</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Status breakdown */}
                <div className="border-r border-b border-zinc-200 bg-white p-6">
                    <p className="label-tiny mb-4">Bestellingen per status</p>
                    {Object.keys(stats.status_counts).length === 0 ? (
                        <p className="text-sm text-zinc-500">Geen bestellingen.</p>
                    ) : (
                        <ul className="space-y-2">
                            {Object.entries(stats.status_counts).map(([s, n]) => (
                                <li key={s} className="flex items-center justify-between text-sm">
                                    <span className="capitalize">{s.replace("_", " ")}</span>
                                    <span className="font-mono-dm">{n}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* === Berichten & moderatie metrics === */}
            <div data-testid="dashboard-messages-block" className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-zinc-200">
                <div className="border-r border-b border-zinc-200 bg-white p-6 relative">
                    <p className="label-tiny">Nieuwe berichten</p>
                    <p className="font-display text-3xl mt-2" data-testid="stat-messages-unhandled">{stats.messages_unhandled ?? 0}</p>
                    <p className="text-xs text-zinc-500 mt-1">wachten op antwoord</p>
                    {(stats.messages_unhandled ?? 0) > 0 && (
                        <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                    )}
                </div>
                <div className="border-r border-b border-zinc-200 bg-white p-6">
                    <p className="label-tiny">Deze week</p>
                    <p className="font-display text-3xl mt-2" data-testid="stat-messages-week">{stats.messages_this_week ?? 0}</p>
                    <p className="text-xs text-zinc-500 mt-1">contactberichten ontvangen</p>
                </div>
                <div className="border-r border-b border-zinc-200 bg-white p-6">
                    <p className="label-tiny">Gem. responstijd</p>
                    <p className="font-display text-3xl mt-2" data-testid="stat-response-time">
                        {stats.avg_response_hours == null ? "—" : (stats.avg_response_hours < 1 ? `${Math.round(stats.avg_response_hours * 60)}m` : `${stats.avg_response_hours}h`)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">over afgehandelde berichten</p>
                </div>
                <div className="border-r border-b border-zinc-200 bg-white p-6 relative">
                    <p className="label-tiny">Galerij moderatie</p>
                    <p className="font-display text-3xl mt-2" data-testid="stat-gallery-pending">{stats.gallery_pending ?? 0}</p>
                    <p className="text-xs text-zinc-500 mt-1">items wachten op goedkeuring</p>
                    {(stats.gallery_pending ?? 0) > 0 && (
                        <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                    )}
                </div>
            </div>
        </div>
    );
}




function GalleryAdmin({ onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending"); // pending | approved | all

    const load = () => {
        setLoading(true);
        api.get("/admin/gallery")
            .then(({ data }) => setItems(data || []))
            .catch(() => toast.error("Kon galerij niet laden"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const toggle = async (g, patch) => {
        try {
            const { data } = await api.patch(`/admin/gallery/${g.id}`, patch);
            setItems(prev => prev.map(x => x.id === g.id ? data : x));
            onChange?.();
            toast.success("Bijgewerkt");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update mislukt");
        }
    };
    const remove = async (g) => {
        if (!window.confirm(`Definitief verwijderen van "${g.user_name}"?`)) return;
        try {
            await api.delete(`/admin/gallery/${g.id}`);
            setItems(prev => prev.filter(x => x.id !== g.id));
            onChange?.();
            toast.success("Verwijderd");
        } catch (err) {
            toast.error("Verwijderen mislukt");
        }
    };

    const filtered = items.filter(i =>
        filter === "pending" ? !i.approved && !i.hidden :
        filter === "approved" ? i.approved :
        true
    );

    return (
        <div data-testid="admin-gallery-section" className="mt-6 space-y-4">
            <div className="border border-zinc-200 bg-white p-5 flex flex-wrap items-center gap-3">
                <p className="label-tiny mr-2">Klantengalerij — moderatie</p>
                {["pending", "approved", "all"].map(f => (
                    <button
                        key={f}
                        data-testid={`gallery-filter-${f}`}
                        onClick={() => setFilter(f)}
                        className={`text-xs px-3 py-1.5 border ${filter === f ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`}>
                        {f === "pending" ? "Wacht op goedkeuring" : f === "approved" ? "Goedgekeurd" : "Alles"}
                        <span className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-4 text-[10px] px-1 rounded-full ${filter === f ? "bg-white text-zinc-950" : "bg-zinc-100"}`}>
                            {items.filter(i =>
                                f === "pending" ? !i.approved && !i.hidden :
                                f === "approved" ? i.approved :
                                true
                            ).length}
                        </span>
                    </button>
                ))}
                <div className="ml-auto text-xs text-zinc-500">Totaal: {items.length}</div>
            </div>

            {loading ? (
                <p className="text-sm text-zinc-500">Laden…</p>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
                    Geen items in deze categorie.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(g => (
                        <div key={g.id} data-testid={`admin-gallery-item-${g.id}`} className="border border-zinc-200 bg-white">
                            <div className="aspect-square bg-zinc-50 border-b border-zinc-100 overflow-hidden">
                                <img src={g.preview_data_url} alt={g.garment_name} className="w-full h-full object-contain" />
                            </div>
                            <div className="p-4 space-y-2">
                                <p className="font-semibold text-sm">{g.user_name}</p>
                                <p className="text-xs text-zinc-500">{g.garment_name}{g.garment_color_name ? ` · ${g.garment_color_name}` : ""}</p>
                                <p className="text-[10px] text-zinc-400 font-mono-dm">{new Date(g.created_at).toLocaleString("nl-BE")}</p>
                                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-100">
                                    {g.approved ? (
                                        <span data-testid={`gallery-status-${g.id}`} className="text-[10px] px-2 py-1 bg-emerald-100 text-emerald-800 font-bold uppercase tracking-widest">Live</span>
                                    ) : (
                                        <span data-testid={`gallery-status-${g.id}`} className="text-[10px] px-2 py-1 bg-amber-100 text-amber-900 font-bold uppercase tracking-widest">Wacht</span>
                                    )}
                                    {!g.approved && (
                                        <button data-testid={`gallery-approve-${g.id}`} onClick={() => toggle(g, { approved: true, hidden: false })}
                                            className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 font-semibold">
                                            Goedkeuren
                                        </button>
                                    )}
                                    {g.approved && (
                                        <button data-testid={`gallery-unapprove-${g.id}`} onClick={() => toggle(g, { approved: false })}
                                            className="text-xs px-3 py-1.5 border border-zinc-300 hover:bg-zinc-50 font-semibold">
                                            Verberg
                                        </button>
                                    )}
                                    <button data-testid={`gallery-delete-${g.id}`} onClick={() => remove(g)}
                                        className="ml-auto text-xs px-3 py-1.5 text-rose-600 hover:bg-rose-50 font-semibold">
                                        Verwijder
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function MessagesAdmin({ onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("inbox"); // inbox | handled | all
    const [active, setActive] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/contact-messages")
            .then(({ data }) => setItems(data || []))
            .catch(() => toast.error("Kon berichten niet laden"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const update = async (m, patch) => {
        try {
            const { data } = await api.patch(`/admin/contact-messages/${m.id}`, patch);
            setItems(prev => prev.map(x => x.id === m.id ? data : x));
            if (active?.id === m.id) setActive(data);
            onChange?.();
            toast.success("Bijgewerkt");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update mislukt");
        }
    };

    const remove = async (m) => {
        if (!window.confirm(`Bericht van "${m.name}" verwijderen?`)) return;
        try {
            await api.delete(`/admin/contact-messages/${m.id}`);
            setItems(prev => prev.filter(x => x.id !== m.id));
            if (active?.id === m.id) setActive(null);
            onChange?.();
            toast.success("Verwijderd");
        } catch {
            toast.error("Verwijderen mislukt");
        }
    };

    const filtered = items.filter(i =>
        filter === "inbox" ? !i.handled :
        filter === "handled" ? i.handled :
        true
    );

    return (
        <div data-testid="admin-messages-section" className="mt-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
            {/* LIJST */}
            <div className="border border-zinc-200 bg-white">
                <div className="p-3 border-b border-zinc-100 flex flex-wrap items-center gap-2">
                    <p className="label-tiny mr-1">Inbox</p>
                    {["inbox", "handled", "all"].map(f => (
                        <button
                            key={f}
                            data-testid={`messages-filter-${f}`}
                            onClick={() => setFilter(f)}
                            className={`text-[10px] px-2.5 py-1 border ${filter === f ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`}>
                            {f === "inbox" ? "Inbox" : f === "handled" ? "Afgehandeld" : "Alles"}
                            <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 text-[9px] px-1 rounded-full ${filter === f ? "bg-white text-zinc-950" : "bg-zinc-100"}`}>
                                {items.filter(i =>
                                    f === "inbox" ? !i.handled :
                                    f === "handled" ? i.handled :
                                    true
                                ).length}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="max-h-[600px] overflow-y-auto">
                    {loading ? (
                        <p className="text-sm text-zinc-500 p-4">Laden…</p>
                    ) : filtered.length === 0 ? (
                        <p className="text-sm text-zinc-400 p-6 text-center" data-testid="messages-empty">Geen berichten.</p>
                    ) : (
                        filtered.map(m => (
                            <button
                                key={m.id}
                                data-testid={`message-row-${m.id}`}
                                onClick={() => setActive(m)}
                                className={`w-full text-left p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${active?.id === m.id ? "bg-zinc-50" : ""} ${!m.handled ? "border-l-2 border-l-rose-500" : ""}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <p className="font-semibold text-sm truncate">{m.name}</p>
                                    <span className="text-[10px] text-zinc-400 font-mono-dm whitespace-nowrap">
                                        {new Date(m.created_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "short" })}
                                    </span>
                                </div>
                                <p className="text-xs text-zinc-500 truncate mt-0.5">{m.subject}</p>
                                <p className="text-xs text-zinc-400 truncate mt-1">{m.message}</p>
                            </button>
                        ))
                    )}
                </div>
            </div>

            {/* DETAIL */}
            <div className="border border-zinc-200 bg-white p-6 min-h-[400px]">
                {!active ? (
                    <div className="h-full flex items-center justify-center text-center">
                        <div>
                            <p className="label-tiny">Geen bericht geselecteerd</p>
                            <p className="font-display text-2xl mt-2">Kies een bericht uit de inbox</p>
                        </div>
                    </div>
                ) : (
                    <div data-testid="message-detail" className="space-y-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <p className="label-tiny">Van</p>
                                <p className="font-display text-3xl mt-1 truncate">{active.name}</p>
                                <a href={`mailto:${active.email}?subject=${encodeURIComponent("Re: " + active.subject)}`}
                                   data-testid="message-reply-email"
                                   className="text-sm text-rose-600 hover:underline break-all">
                                    {active.email}
                                </a>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {active.handled ? (
                                    <span className="text-[10px] px-2 py-1 bg-emerald-100 text-emerald-800 font-bold uppercase tracking-widest">Afgehandeld</span>
                                ) : (
                                    <span className="text-[10px] px-2 py-1 bg-rose-100 text-rose-800 font-bold uppercase tracking-widest">Nieuw</span>
                                )}
                            </div>
                        </div>

                        <div className="border-t border-zinc-100 pt-4">
                            <p className="label-tiny">Onderwerp</p>
                            <p className="font-semibold mt-1">{active.subject}</p>
                        </div>

                        <div>
                            <p className="label-tiny mb-2">Bericht</p>
                            <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed bg-zinc-50 border border-zinc-100 p-4">
                                {active.message}
                            </p>
                        </div>

                        <div className="text-[10px] text-zinc-400 font-mono-dm">
                            Ontvangen: {new Date(active.created_at).toLocaleString("nl-BE")}
                            {active.ip && <> · IP: {active.ip}</>}
                        </div>

                        <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-100">
                            <a
                                href={`mailto:${active.email}?subject=${encodeURIComponent("Re: " + active.subject)}`}
                                data-testid="message-reply-cta"
                                className="btn-primary inline-flex items-center gap-2">
                                Antwoord per mail
                            </a>
                            {!active.handled ? (
                                <button
                                    data-testid="message-mark-handled"
                                    onClick={() => update(active, { handled: true })}
                                    className="text-xs px-3 py-2.5 bg-emerald-600 text-white hover:bg-emerald-700 font-bold uppercase tracking-widest">
                                    Markeer afgehandeld
                                </button>
                            ) : (
                                <button
                                    data-testid="message-reopen"
                                    onClick={() => update(active, { handled: false })}
                                    className="text-xs px-3 py-2.5 border border-zinc-300 hover:bg-zinc-50 font-bold uppercase tracking-widest">
                                    Heropen
                                </button>
                            )}
                            <button
                                data-testid="message-delete"
                                onClick={() => remove(active)}
                                className="ml-auto text-xs px-3 py-2.5 text-rose-600 hover:bg-rose-50 font-bold uppercase tracking-widest">
                                Verwijderen
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function WorkwearAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brand, setBrand] = useState("all");
    const [editingStock, setEditingStock] = useState(null); // {id, stock}

    const load = () => {
        setLoading(true);
        api.get("/admin/workwear")
            .then(({ data }) => setItems(data || []))
            .catch(() => toast.error("Kon werkkledij niet laden"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const update = async (w, patch) => {
        try {
            const { data } = await api.patch(`/admin/workwear/${w.id}`, patch);
            setItems(prev => prev.map(x => x.id === w.id ? data : x));
            toast.success("Bijgewerkt");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update mislukt");
        }
    };

    const remove = async (w) => {
        if (!window.confirm(`Definitief verwijderen: ${w.name} (${w.sku})?`)) return;
        try {
            await api.delete(`/admin/workwear/${w.id}`);
            setItems(prev => prev.filter(x => x.id !== w.id));
            toast.success("Verwijderd");
        } catch {
            toast.error("Verwijderen mislukt");
        }
    };

    const totalStock = (w) => Object.values(w.stock || {}).reduce((s, n) => s + (n || 0), 0);

    const filtered = brand === "all" ? items : items.filter(i => i.brand === brand);

    return (
        <div data-testid="admin-workwear-section" className="mt-6 space-y-4">
            <div className="border border-zinc-200 bg-white p-5 flex flex-wrap items-center gap-3">
                <p className="label-tiny mr-2">Werkkledij — prijzen, voorraad &amp; bulk</p>
                {["all", "herock", "portwest"].map(b => (
                    <button
                        key={b}
                        data-testid={`workwear-admin-filter-${b}`}
                        onClick={() => setBrand(b)}
                        className={`text-xs px-3 py-1.5 border ${brand === b ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`}>
                        {b === "all" ? "Alles" : b.charAt(0).toUpperCase() + b.slice(1)}
                        <span className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-4 text-[10px] px-1 rounded-full ${brand === b ? "bg-white text-zinc-950" : "bg-zinc-100"}`}>
                            {b === "all" ? items.length : items.filter(i => i.brand === b).length}
                        </span>
                    </button>
                ))}
                <p className="ml-auto text-[10px] text-zinc-400">Tip: prijzen worden direct opgeslagen na blur. Klik op de voorraad-cel om per maat aan te passen.</p>
            </div>

            {loading ? (
                <p className="text-sm text-zinc-500">Laden…</p>
            ) : (
                <div className="border border-zinc-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm min-w-[860px]">
                        <thead>
                            <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-500">
                                <th className="p-3">Foto</th>
                                <th className="p-3">Merk / SKU</th>
                                <th className="p-3">Naam</th>
                                <th className="p-3 text-right">Prijs (€)</th>
                                <th className="p-3 text-center">Voorraad</th>
                                <th className="p-3 text-center">Actief</th>
                                <th className="p-3 text-right">Acties</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(w => {
                                const total = totalStock(w);
                                return (
                                <tr key={w.id} data-testid={`workwear-admin-row-${w.sku}`} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/50">
                                    <td className="p-3">
                                        <div className="w-12 h-14 bg-zinc-50 border border-zinc-200">
                                            <img src={w.image_url} alt={w.name} className="w-full h-full object-contain" loading="lazy" />
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <span className={`text-[9px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 ${w.brand === "herock" ? "bg-orange-500" : "bg-blue-700"}`}>
                                            {w.brand}
                                        </span>
                                        <p className="font-mono-dm text-xs mt-1">{w.sku}</p>
                                    </td>
                                    <td className="p-3">
                                        <p className="font-semibold">{w.name}</p>
                                        <p className="text-[10px] text-zinc-500 mt-0.5">{w.category}</p>
                                    </td>
                                    <td className="p-3 text-right">
                                        <input
                                            data-testid={`workwear-price-${w.sku}`}
                                            type="number"
                                            step="0.50"
                                            defaultValue={w.price}
                                            onBlur={(e) => {
                                                const v = parseFloat(e.target.value);
                                                if (!isNaN(v) && v !== w.price) update(w, { price: v });
                                            }}
                                            className="input-flat text-right w-24"
                                        />
                                    </td>
                                    <td className="p-3 text-center">
                                        <button
                                            data-testid={`workwear-stock-${w.sku}`}
                                            onClick={() => setEditingStock({ id: w.id, sku: w.sku, sizes: w.sizes, stock: { ...(w.stock || {}) } })}
                                            className={`text-xs font-bold px-3 py-1.5 border ${total === 0 ? "bg-rose-50 border-rose-200 text-rose-700" : total <= 5 ? "bg-amber-50 border-amber-200 text-amber-800" : "border-zinc-300 hover:border-zinc-950"}`}>
                                            {total} stuks
                                            <span className="block text-[9px] font-normal text-zinc-400 mt-0.5">klik om aan te passen</span>
                                        </button>
                                    </td>
                                    <td className="p-3 text-center">
                                        <button
                                            data-testid={`workwear-active-${w.sku}`}
                                            onClick={() => update(w, { active: !w.active })}
                                            className={`text-[10px] px-2 py-1 font-bold uppercase tracking-widest ${w.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>
                                            {w.active ? "Live" : "Verborgen"}
                                        </button>
                                    </td>
                                    <td className="p-3 text-right">
                                        <button
                                            data-testid={`workwear-delete-${w.sku}`}
                                            onClick={() => remove(w)}
                                            className="text-[10px] text-rose-600 hover:bg-rose-50 px-2 py-1 font-semibold uppercase tracking-widest">
                                            Verwijder
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Stock editor modal */}
            {editingStock && (
                <StockEditorModal
                    state={editingStock}
                    setState={setEditingStock}
                    onSave={async (newStock) => {
                        try {
                            const { data } = await api.patch(`/admin/workwear/${editingStock.id}`, { stock: newStock });
                            setItems(prev => prev.map(x => x.id === editingStock.id ? data : x));
                            toast.success("Voorraad bijgewerkt");
                            setEditingStock(null);
                        } catch (err) {
                            toast.error(err?.response?.data?.detail || "Update mislukt");
                        }
                    }}
                />
            )}

            {/* Bulk staffel-config */}
            <WorkwearBulkConfig />
        </div>
    );
}

function StockEditorModal({ state, setState, onSave }) {
    const { sizes, stock } = state;
    const setQty = (size, val) => {
        const n = Math.max(0, parseInt(val || 0, 10));
        setState({ ...state, stock: { ...stock, [size]: n } });
    };
    return (
        <div onClick={() => setState(null)} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in">
            <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-md w-full" data-testid="stock-editor-modal">
                <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
                    <div>
                        <p className="label-tiny">Voorraad bewerken</p>
                        <p className="font-display text-2xl mt-1">{state.sku}</p>
                    </div>
                    <button onClick={() => setState(null)} className="text-zinc-400 hover:text-zinc-950">✕</button>
                </div>
                <div className="p-5 space-y-3">
                    {sizes.map(s => (
                        <div key={s} className="flex items-center gap-3">
                            <span className="w-16 font-bold text-sm uppercase tracking-widest">{s}</span>
                            <input
                                data-testid={`stock-input-${s}`}
                                type="number"
                                min="0"
                                value={stock[s] ?? 0}
                                onChange={(e) => setQty(s, e.target.value)}
                                className="input-flat flex-1"
                            />
                            <span className="text-xs text-zinc-400">stuks</span>
                        </div>
                    ))}
                    <p className="text-[10px] text-zinc-400 pt-2">
                        Totaal: <span className="font-bold text-zinc-900">{Object.values(stock).reduce((s, n) => s + (parseInt(n || 0)), 0)} stuks</span>
                    </p>
                </div>
                <div className="p-5 border-t border-zinc-100 flex justify-end gap-2">
                    <button onClick={() => setState(null)} className="btn-ghost text-xs">Annuleer</button>
                    <button
                        data-testid="stock-save"
                        onClick={() => onSave(Object.fromEntries(Object.entries(stock).map(([k, v]) => [k, parseInt(v || 0, 10)])))}
                        className="btn-primary text-xs">
                        Opslaan
                    </button>
                </div>
            </div>
        </div>
    );
}

function WorkwearBulkConfig() {
    const [tiers, setTiers] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/promo/config")
            .then(({ data }) => { setTiers(data.workwear_volume_tiers || []); setLoaded(true); })
            .catch(() => setLoaded(true));
    }, []);

    const setTier = (idx, key, val) => {
        const next = tiers.slice();
        next[idx] = { ...next[idx], [key]: parseInt(val || 0, 10) };
        setTiers(next);
    };
    const addTier = () => setTiers([...tiers, { min_qty: 5, percent: 5 }]);
    const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));

    const save = async () => {
        try {
            await api.put("/admin/promo/config", { workwear_volume_tiers: tiers });
            toast.success("Werkkledij-staffel opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        }
    };

    if (!loaded) return null;

    return (
        <div data-testid="workwear-bulk-config" className="border border-zinc-200 bg-white p-5">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <p className="label-tiny">★ Bulkkorting werkkledij</p>
                    <p className="text-xs text-zinc-500 mt-1">Korting op de werkkledij-regels in het mandje, gebaseerd op totaal aantal werkkledij stuks.</p>
                </div>
                <button onClick={addTier} className="btn-ghost text-xs" data-testid="workwear-tier-add">+ Niveau</button>
            </div>
            <div className="space-y-2">
                {tiers.length === 0 ? (
                    <p className="text-xs text-zinc-400 italic">Nog geen staffels — klik op &quot;+ Niveau&quot;.</p>
                ) : tiers.sort((a,b) => a.min_qty - b.min_qty).map((t, idx) => (
                    <div key={idx} className="flex items-center gap-3" data-testid={`workwear-tier-row-${idx}`}>
                        <span className="text-xs text-zinc-500 w-20">Vanaf</span>
                        <input
                            type="number"
                            min="1"
                            value={t.min_qty}
                            onChange={(e) => setTier(idx, "min_qty", e.target.value)}
                            className="input-flat w-20 text-center"
                            data-testid={`workwear-tier-qty-${idx}`}
                        />
                        <span className="text-xs text-zinc-500">stuks →</span>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            value={t.percent}
                            onChange={(e) => setTier(idx, "percent", e.target.value)}
                            className="input-flat w-20 text-center"
                            data-testid={`workwear-tier-percent-${idx}`}
                        />
                        <span className="text-xs text-zinc-500">% korting</span>
                        <button onClick={() => removeTier(idx)} className="ml-auto text-rose-600 text-xs" data-testid={`workwear-tier-remove-${idx}`}>Verwijder</button>
                    </div>
                ))}
            </div>
            <div className="pt-4 border-t border-zinc-100 mt-4">
                <button onClick={save} className="btn-primary text-xs" data-testid="workwear-tiers-save">Staffel opslaan</button>
            </div>
        </div>
    );
}


function AtelierAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/atelier-photos")
            .then(({ data }) => setItems(data || []))
            .catch(() => toast.error("Kon atelier-foto's niet laden"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const toggleActive = async (p) => {
        try {
            const { data } = await api.patch(`/admin/atelier-photos/${p.id}`, { active: !p.active });
            setItems(prev => prev.map(x => x.id === p.id ? data : x));
        } catch {
            toast.error("Update mislukt");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Definitief verwijderen: "${p.title || "foto"}"?`)) return;
        try {
            await api.delete(`/admin/atelier-photos/${p.id}`);
            setItems(prev => prev.filter(x => x.id !== p.id));
            toast.success("Verwijderd");
        } catch {
            toast.error("Verwijderen mislukt");
        }
    };

    return (
        <div data-testid="admin-atelier-section" className="mt-6 space-y-4">
            <div className="border border-zinc-200 bg-white p-5 flex flex-wrap items-center gap-3">
                <div>
                    <p className="label-tiny">Atelier galerij — eigen foto's voor de showcase</p>
                    <p className="text-xs text-zinc-500 mt-1">Foto's verschijnen op de tab &quot;Uit ons atelier&quot; op de publieke <code className="bg-zinc-100 px-1">/gallery</code> pagina.</p>
                </div>
                <button
                    data-testid="atelier-add-open"
                    onClick={() => setAdding(true)}
                    className="btn-primary text-xs inline-flex items-center gap-2 ml-auto">
                    + Nieuwe foto
                </button>
            </div>

            {loading ? (
                <p className="text-sm text-zinc-500">Laden…</p>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-zinc-300 bg-white p-12 text-center">
                    <p className="font-display text-2xl">Nog geen foto's</p>
                    <p className="text-sm text-zinc-500 mt-2">Klik &quot;Nieuwe foto&quot; om je eerste atelier-foto te uploaden.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map(p => (
                        <div key={p.id} data-testid={`admin-atelier-card-${p.id}`} className="border border-zinc-200 bg-white overflow-hidden">
                            <div className="aspect-[4/5] bg-zinc-50 overflow-hidden">
                                <img src={p.image_data_url} alt={p.title || "Atelier foto"} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-4 space-y-2">
                                {p.title && <p className="font-semibold text-sm truncate">{p.title}</p>}
                                {p.caption && <p className="text-xs text-zinc-500 line-clamp-2">{p.caption}</p>}
                                {(p.tags || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {p.tags.map(t => <span key={t} className="text-[9px] uppercase tracking-widest bg-zinc-100 px-1.5 py-0.5">{t}</span>)}
                                    </div>
                                )}
                                <p className="text-[10px] text-zinc-400 font-mono-dm">Sort: {p.sort_order} · {new Date(p.created_at).toLocaleDateString("nl-BE")}</p>
                                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-100">
                                    <button
                                        data-testid={`atelier-toggle-${p.id}`}
                                        onClick={() => toggleActive(p)}
                                        className={`text-[10px] px-2 py-1 font-bold uppercase tracking-widest ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>
                                        {p.active ? "Live" : "Verborgen"}
                                    </button>
                                    <button
                                        data-testid={`atelier-edit-${p.id}`}
                                        onClick={() => setEditing(p)}
                                        className="text-[10px] px-2 py-1 border border-zinc-300 hover:border-zinc-950 font-bold uppercase tracking-widest">
                                        Bewerk
                                    </button>
                                    <button
                                        data-testid={`atelier-delete-${p.id}`}
                                        onClick={() => remove(p)}
                                        className="ml-auto text-[10px] text-rose-600 hover:bg-rose-50 px-2 py-1 font-bold uppercase tracking-widest">
                                        ✕
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {adding && (
                <AtelierEditorModal
                    photo={null}
                    onClose={() => setAdding(false)}
                    onSaved={(p) => { setItems(prev => [p, ...prev]); setAdding(false); }}
                />
            )}
            {editing && (
                <AtelierEditorModal
                    photo={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(p) => { setItems(prev => prev.map(x => x.id === p.id ? p : x)); setEditing(null); }}
                />
            )}
        </div>
    );
}

function AtelierEditorModal({ photo, onClose, onSaved }) {
    const isNew = !photo;
    const [form, setForm] = useState({
        image_data_url: photo?.image_data_url || "",
        title: photo?.title || "",
        caption: photo?.caption || "",
        tags: (photo?.tags || []).join(", "),
        sort_order: photo?.sort_order ?? 0,
        active: photo?.active !== false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [resizing, setResizing] = useState(false);

    const compressImage = (file, maxWidth = 1600, quality = 0.82) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = reject;
            img.onload = () => {
                const scale = Math.min(1, maxWidth / img.width);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement("canvas");
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL("image/jpeg", quality));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Alleen afbeeldingen toegestaan"); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
        setResizing(true);
        try {
            const dataUrl = await compressImage(file);
            setForm(f => ({ ...f, image_data_url: dataUrl }));
        } catch {
            toast.error("Foto kon niet verwerkt worden");
        } finally {
            setResizing(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.image_data_url) { toast.error("Upload eerst een foto"); return; }
        setSubmitting(true);
        try {
            const payload = {
                image_data_url: form.image_data_url,
                title: form.title.trim().slice(0, 120),
                caption: form.caption.trim().slice(0, 500),
                tags: form.tags.split(",").map(t => t.trim()).filter(Boolean).slice(0, 8),
                sort_order: parseInt(form.sort_order || 0, 10),
                active: !!form.active,
            };
            const { data } = isNew
                ? await api.post("/admin/atelier-photos", payload)
                : await api.patch(`/admin/atelier-photos/${photo.id}`, payload);
            onSaved(data);
            toast.success(isNew ? "Foto toegevoegd" : "Foto bijgewerkt");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div onClick={onClose} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in overflow-y-auto">
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                data-testid="atelier-editor-modal"
                className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <p className="label-tiny">{isNew ? "Nieuw" : "Bewerken"}</p>
                        <p className="font-display text-2xl mt-1">{isNew ? "Nieuwe atelier foto" : form.title || "Foto"}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-950">✕</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0">
                    {/* Preview kolom */}
                    <div className="p-6 border-r border-zinc-100 bg-zinc-50/60 space-y-4">
                        <p className="label-tiny">Voorbeeld</p>
                        <div className="aspect-[4/5] bg-white border border-zinc-200 flex items-center justify-center overflow-hidden">
                            {form.image_data_url ? (
                                <img src={form.image_data_url} alt="preview" className="w-full h-full object-cover" />
                            ) : resizing ? (
                                <p className="text-xs text-zinc-500">Foto comprimeren…</p>
                            ) : (
                                <p className="text-xs text-zinc-400 text-center p-4">Upload een foto hieronder</p>
                            )}
                        </div>
                        <label className="block">
                            <span className="label-tiny">Foto uploaden (max 10MB)</span>
                            <input
                                data-testid="atelier-modal-file"
                                type="file"
                                accept="image/*"
                                onChange={onFile}
                                className="mt-2 text-xs file:btn-primary file:border-0 file:cursor-pointer"
                            />
                            <p className="text-[10px] text-zinc-400 mt-1.5 leading-snug">
                                💡 We comprimeren je foto automatisch naar max 1600px breed (JPEG 82%) — sneller laden voor klanten.
                            </p>
                        </label>
                    </div>

                    {/* Velden kolom */}
                    <div className="p-6 space-y-5">
                        <div>
                            <label className="label-tiny block mb-2">Titel</label>
                            <input
                                data-testid="atelier-modal-title"
                                type="text"
                                maxLength={120}
                                value={form.title}
                                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="Bijv. Bachelor party tees — juni 2026"
                                className="input-flat"
                            />
                        </div>

                        <div>
                            <label className="label-tiny block mb-2">Beschrijving</label>
                            <textarea
                                data-testid="atelier-modal-caption"
                                rows={3}
                                maxLength={500}
                                value={form.caption}
                                onChange={(e) => setForm(f => ({ ...f, caption: e.target.value }))}
                                placeholder="Korte beschrijving van het ontwerp of de klantopdracht…"
                                className="input-flat resize-y"
                            />
                        </div>

                        <div>
                            <label className="label-tiny block mb-2">Tags (komma-gescheiden, max 8)</label>
                            <input
                                data-testid="atelier-modal-tags"
                                type="text"
                                value={form.tags}
                                onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                                placeholder="bv. voetbal, jubileum, familie"
                                className="input-flat"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-tiny block mb-2">Sorteer-volgorde</label>
                                <input
                                    data-testid="atelier-modal-sort"
                                    type="number"
                                    value={form.sort_order}
                                    onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))}
                                    className="input-flat"
                                />
                                <p className="text-[10px] text-zinc-400 mt-1">Lager = eerst zichtbaar</p>
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Status</label>
                                <button
                                    type="button"
                                    data-testid="atelier-modal-active"
                                    onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                                    className={`w-full px-3 py-2 text-xs font-bold uppercase tracking-widest ${form.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>
                                    {form.active ? "Live" : "Verborgen"}
                                </button>
                            </div>
                        </div>

                        <div className="border-t border-zinc-100 pt-4 flex flex-wrap justify-end gap-2">
                            <button type="button" onClick={onClose} className="btn-ghost">Annuleer</button>
                            <button type="submit" disabled={submitting || resizing || !form.image_data_url}
                                data-testid="atelier-modal-save"
                                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                                {submitting ? "Bezig…" : (isNew ? "Foto toevoegen" : "Wijzigingen opslaan")}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}


// ===== Accessoires beheer (transfertape, spray, reiniger, lijmverwijderaar) =====
function AccessoriesAdmin({ accessories, onUpdated, onAdded, onDeleted }) {
    const [showForm, setShowForm] = useState(false);
    return (
        <div data-testid="admin-accessories" className="mt-6 space-y-3">
            <div className="border border-zinc-200 bg-white p-4 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny text-rose-600">★ Accessoires</p>
                    <p className="text-xs text-zinc-600 mt-1">
                        Beheer prijzen, voorraad en zichtbaarheid van de accessoires die klanten kunnen bestellen op de Stickers-pagina.
                    </p>
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    data-testid="accessory-new-btn"
                    className="btn-primary text-xs">
                    + Nieuw accessoire
                </button>
            </div>

            {showForm && (
                <AccessoryForm
                    onCancel={() => setShowForm(false)}
                    onSaved={(a) => { onAdded(a); setShowForm(false); }}
                />
            )}

            {(!accessories || accessories.length === 0) ? (
                <div data-testid="admin-accessories-empty" className="border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500">
                    Nog geen accessoires. Voeg je eerste accessoire hierboven toe.
                </div>
            ) : (
                <div className="border border-zinc-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-500">
                                <th className="p-4">Product</th>
                                <th className="p-4">Eenheid</th>
                                <th className="p-4 w-32">Prijs (€)</th>
                                <th className="p-4 w-52">Voorraad</th>
                                <th className="p-4 w-32">Zichtbaar</th>
                                <th className="p-4 w-32">Acties</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accessories.map(a => (
                                <AccessoryRow key={a.id} accessory={a} accessories={accessories} onUpdated={onUpdated} onDeleted={onDeleted} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AccessoryForm({ accessory, onCancel, onSaved }) {
    const empty = {
        slug: "", name: "", short: "", description: "", unit: "",
        price: 0, image_url: "", icon: "spray",
        in_stock: true, stock_count: "", visible: true, sort_order: 0,
        supplier_url: "",
        occasions: [], recipients: [],
    };
    const [draft, setDraft] = useState(accessory ? { ...accessory, stock_count: accessory.stock_count ?? "", occasions: accessory.occasions || [], recipients: accessory.recipients || [] } : empty);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!draft.name.trim()) { toast.error("Naam is verplicht"); return; }
        setSaving(true);
        try {
            const payload = {
                slug: (draft.slug || "").trim(),
                name: draft.name.trim(),
                short: (draft.short || "").trim(),
                description: (draft.description || "").trim(),
                unit: (draft.unit || "").trim(),
                price: parseFloat(draft.price) || 0,
                icon: draft.icon || "spray",
                image_url: (draft.image_url || "").trim(),
                in_stock: !!draft.in_stock,
                stock_count: draft.stock_count === "" || draft.stock_count === null ? null : Math.max(0, parseInt(draft.stock_count) || 0),
                visible: !!draft.visible,
                sort_order: parseInt(draft.sort_order) || 0,
                supplier_url: (draft.supplier_url || "").trim(),
                occasions: draft.occasions || [],
                recipients: draft.recipients || [],
            };
            const { data } = accessory
                ? await api.patch(`/admin/accessories/${accessory.id}`, payload)
                : await api.post("/admin/accessories", payload);
            onSaved(data);
            toast.success(accessory ? "Bijgewerkt" : "Accessoire toegevoegd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-zinc-950 bg-zinc-50 p-5" data-testid="accessory-form">
            <p className="label-tiny mb-4">{accessory ? "Bewerk accessoire" : "Nieuw accessoire"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                    <div>
                        <label className="label-tiny block mb-1">Naam *</label>
                        <input className="input-flat" value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            data-testid="accessory-name"
                            placeholder="Bv. Transfertape Premium" />
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Korte beschrijving (1 zin)</label>
                        <input className="input-flat" value={draft.short}
                            onChange={(e) => setDraft({ ...draft, short: e.target.value })}
                            maxLength={140}
                            placeholder="Bv. Sterke, doorzichtige tape voor het overzetten van vinyl" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-1">Eenheid</label>
                            <input className="input-flat" value={draft.unit}
                                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                                placeholder="Bv. rol 33cm × 5m" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-1">Prijs (€) *</label>
                            <input type="number" step="0.01" min="0" className="input-flat" value={draft.price}
                                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                                data-testid="accessory-price" />
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Icoon-type</label>
                        <select className="input-flat" value={draft.icon}
                            onChange={(e) => setDraft({ ...draft, icon: e.target.value })}>
                            <option value="spray">Spray</option>
                            <option value="tape">Tape</option>
                            <option value="cleaner">Reiniger</option>
                            <option value="remover">Remover</option>
                            <option value="color-card">Kleurenkaart</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="label-tiny block mb-1">Productfoto</label>
                        <ImageUpload
                            value={draft.image_url}
                            onChange={(url) => setDraft({ ...draft, image_url: url })}
                            testId="accessory-image"
                        />
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Volledige beschrijving</label>
                        <textarea className="input-flat min-h-[70px]" value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Nabestel-link (intern voor admin)</label>
                        <input className="input-flat" value={draft.supplier_url}
                            onChange={(e) => setDraft({ ...draft, supplier_url: e.target.value })}
                            placeholder="https://creaplot.nl/..." />
                    </div>
                    <div className="pt-3 border-t border-zinc-200">
                        <label className="label-tiny block mb-1">Stock teller (laat leeg = niet tellen)</label>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setDraft({ ...draft, stock_count: Math.max(0, (parseInt(draft.stock_count) || 0) - 1) })}
                                className="w-8 h-8 border border-zinc-200 hover:bg-zinc-50 text-sm font-bold">−</button>
                            <input type="number" min="0" className="border border-zinc-200 px-2 py-1.5 w-24 text-center font-mono-dm"
                                value={draft.stock_count}
                                onChange={(e) => setDraft({ ...draft, stock_count: e.target.value })}
                                placeholder="∞"
                                data-testid="accessory-stock" />
                            <button type="button" onClick={() => setDraft({ ...draft, stock_count: (parseInt(draft.stock_count) || 0) + 1 })}
                                className="w-8 h-8 border border-zinc-200 hover:bg-zinc-50 text-sm font-bold">+</button>
                            <button type="button" onClick={() => setDraft({ ...draft, stock_count: "" })}
                                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-950 underline ml-2">
                                Niet tellen
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-xs">
                        <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={draft.visible}
                                onChange={(e) => setDraft({ ...draft, visible: e.target.checked })} />
                            Zichtbaar in shop
                        </label>
                        <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={draft.in_stock}
                                onChange={(e) => setDraft({ ...draft, in_stock: e.target.checked })} />
                            Op voorraad (vlag)
                        </label>
                        <label className="inline-flex items-center gap-2">
                            Volgorde:
                            <input type="number" className="w-16 border border-zinc-200 px-2 py-1"
                                value={draft.sort_order}
                                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
                        </label>
                    </div>

                    <div className="pt-3 border-t border-zinc-200">
                        <p className="label-tiny mb-2 text-rose-600">★ Cadeau-vinder tags</p>
                        <p className="text-[11px] text-zinc-500 mb-2">
                            Selecteer voor welke gelegenheden en ontvangers dit accessoire geschikt is — het verschijnt dan in de Cadeau-vinder.
                        </p>
                        <GiftTagPicker
                            occasions={draft.occasions || []}
                            recipients={draft.recipients || []}
                            onChange={({ occasions, recipients }) => setDraft({ ...draft, occasions, recipients })}
                        />
                    </div>
                </div>
            </div>
            <div className="mt-5 flex gap-2">
                <button onClick={save} disabled={saving} data-testid="accessory-save"
                    className="btn-primary text-xs">{saving ? "Opslaan..." : (accessory ? "Bijwerken" : "Toevoegen")}</button>
                <button onClick={onCancel} className="btn-ghost text-xs">Annuleer</button>
            </div>
        </div>
    );
}

function AccessoryRow({ accessory, accessories, onUpdated, onDeleted }) {
    const [name, setName] = useState(accessory.name || "");
    const [short, setShort] = useState(accessory.short || "");
    const [description, setDescription] = useState(accessory.description || "");
    const [unit, setUnit] = useState(accessory.unit || "");
    const [imageUrl, setImageUrl] = useState(accessory.image_url || "");
    const [price, setPrice] = useState(String(accessory.price ?? 0));
    const [inStock, setInStock] = useState(!!accessory.in_stock);
    const [visible, setVisible] = useState(accessory.visible !== false);
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/accessories/${accessory.id}`, {
                name,
                short,
                description,
                unit,
                image_url: imageUrl,
                price: parseFloat(price),
                in_stock: inStock,
                visible,
            });
            onUpdated(data);
            toast.success(`${data.name} bijgewerkt`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <tr data-testid={`acc-row-${accessory.slug}`} className="border-b border-zinc-100">
                <td className="p-4 align-top">
                    <input
                        data-testid={`acc-name-${accessory.slug}`}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-2 py-1.5 border border-zinc-300 text-sm font-semibold focus:outline-none focus:border-zinc-950"
                    />
                    <p className="text-[10px] text-zinc-400 mt-1 font-mono-dm">{accessory.slug}</p>
                </td>
                <td className="p-4 align-top">
                    <input
                        data-testid={`acc-unit-${accessory.slug}`}
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="w-40 px-2 py-1.5 border border-zinc-300 text-xs focus:outline-none focus:border-zinc-950"
                        placeholder="bv. fles 1 L"
                    />
                </td>
                <td className="p-4 align-top">
                    <input
                        data-testid={`acc-price-${accessory.slug}`}
                        type="number" step="0.05" min="0"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="w-24 px-2 py-1.5 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950"
                    />
                </td>
                <td className="p-4 align-top">
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                        <input
                            data-testid={`acc-stock-${accessory.slug}`}
                            type="checkbox" checked={inStock}
                            onChange={(e) => setInStock(e.target.checked)}
                            className="w-4 h-4 accent-zinc-950"
                        />
                        {inStock ? "Op voorraad" : "Op bestelling"}
                    </label>
                    <StockAdjuster product={accessory} endpoint="/admin/accessories" onUpdated={onUpdated} />
                </td>
                <td className="p-4 align-top">
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                        <input
                            data-testid={`acc-visible-${accessory.slug}`}
                            type="checkbox" checked={visible}
                            onChange={(e) => setVisible(e.target.checked)}
                            className="w-4 h-4 accent-zinc-950"
                        />
                        Toon op site
                    </label>
                </td>
                <td className="p-4 align-top">
                    <div className="flex flex-col gap-1.5">
                        <ReorderArrows item={accessory} items={accessories || []} endpoint="/admin/accessories" onUpdated={onUpdated} testId="acc-reorder" />
                        <button
                            data-testid={`acc-toggle-desc-${accessory.slug}`}
                            onClick={() => setExpanded(v => !v)}
                            className="text-[10px] px-3 py-1.5 border border-zinc-300 hover:border-zinc-950 font-bold uppercase tracking-widest">
                            {expanded ? "Verberg" : "Beschrijving"}
                        </button>
                        <button
                            data-testid={`acc-save-${accessory.slug}`}
                            onClick={save} disabled={saving}
                            className="text-[10px] px-3 py-1.5 bg-zinc-950 text-white font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-60">
                            {saving ? "…" : "Opslaan"}
                        </button>
                        <button
                            data-testid={`acc-delete-${accessory.slug}`}
                            onClick={async () => {
                                if (!window.confirm(`Verwijder "${accessory.name}"?`)) return;
                                try {
                                    await api.delete(`/admin/accessories/${accessory.id}`);
                                    onDeleted && onDeleted(accessory.id);
                                    toast.success("Verwijderd");
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Mislukt");
                                }
                            }}
                            className="text-[10px] px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold uppercase tracking-widest">
                            Verwijder
                        </button>
                    </div>
                </td>
            </tr>
            {expanded && (
                <tr data-testid={`acc-desc-row-${accessory.slug}`} className="border-b border-zinc-200 bg-zinc-50">
                    <td colSpan={6} className="p-5 space-y-3">
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
                            <div>
                                <p className="label-tiny mb-1.5">Productafbeelding (URL)</p>
                                <input
                                    data-testid={`acc-image-${accessory.slug}`}
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder="https://www.creaplot.nl/...product.jpg"
                                    className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 font-mono-dm"
                                />
                                <p className="text-[10px] text-zinc-400 mt-0.5">Plak een directe link naar een productafbeelding (jpg/png). Leeg = toon icoon.</p>
                            </div>
                            {imageUrl && (
                                <div className="w-24 h-24 border border-zinc-300 bg-white overflow-hidden">
                                    <img src={imageUrl} alt="preview" className="w-full h-full object-contain" onError={(e) => { e.target.style.opacity = "0.2"; }} />
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="label-tiny mb-1.5">Korte beschrijving (1 zin — toont op productkaart)</p>
                            <input
                                data-testid={`acc-short-${accessory.slug}`}
                                value={short}
                                onChange={(e) => setShort(e.target.value)}
                                className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
                                maxLength={140}
                            />
                            <p className="text-[10px] text-zinc-400 mt-0.5">{short.length}/140 tekens</p>
                        </div>
                        <div>
                            <p className="label-tiny mb-1.5">Volledige beschrijving (toont op productpagina)</p>
                            <textarea
                                data-testid={`acc-description-${accessory.slug}`}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 resize-y"
                            />
                        </div>
                        <p className="text-[11px] text-zinc-500 italic">
                            Tip: klik op &ldquo;Opslaan&rdquo; rechts in de rij om de wijzigingen door te voeren.
                        </p>
                    </td>
                </tr>
            )}
        </>
    );
}


// ===== Supplier nabestel-links (admin only) =====
function SupplierLinksAdmin({ links }) {
    const grouped = (links || []).reduce((acc, l) => {
        (acc[l.category] = acc[l.category] || []).push(l);
        return acc;
    }, {});
    return (
        <div data-testid="admin-supplier-links" className="mt-6 space-y-6">
            <div className="border border-amber-200 bg-amber-50 p-4">
                <p className="label-tiny text-amber-700">★ Admin-only — niet zichtbaar voor klanten</p>
                <p className="font-display text-2xl mt-1">Snel nabestellen bij leverancier.</p>
                <p className="text-sm text-zinc-700 mt-2 max-w-2xl">
                    Klik op een knop om direct naar de leverancier (Creaplot) te gaan en je voorraad vinyl, applicatietape
                    en verzorgingsproducten bij te bestellen. De links openen in een nieuw tabblad.
                </p>
            </div>

            {Object.keys(grouped).map(cat => (
                <div key={cat} className="border border-zinc-200 bg-white">
                    <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
                        <p className="label-tiny text-rose-600">★ {cat}</p>
                    </div>
                    <div className="divide-y divide-zinc-100">
                        {grouped[cat].map(l => (
                            <div key={l.url} className="p-5 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex-1 min-w-[260px]">
                                    <p className="font-semibold">{l.label}</p>
                                    <p className="text-xs text-zinc-500 mt-1">{l.note}</p>
                                    <p className="text-[10px] text-zinc-400 mt-1.5 font-mono-dm truncate max-w-md">{l.url}</p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button
                                        data-testid={`supplier-copy-${l.label.replace(/\W+/g,'-').toLowerCase()}`}
                                        onClick={() => { navigator.clipboard.writeText(l.url); toast.success("Link gekopieerd"); }}
                                        className="text-[10px] px-3 py-2 border border-zinc-300 hover:border-zinc-950 font-bold uppercase tracking-widest">
                                        Kopieer link
                                    </button>
                                    <a
                                        data-testid={`supplier-open-${l.label.replace(/\W+/g,'-').toLowerCase()}`}
                                        href={l.url} target="_blank" rel="noopener noreferrer"
                                        className="text-[10px] px-3 py-2 bg-zinc-950 text-white hover:bg-zinc-800 font-bold uppercase tracking-widest inline-flex items-center gap-1.5">
                                        Bestel nu →
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}



// ===== Leveranciers (suppliers) — adressenboek met email/telefoon/website =====
function SuppliersAdmin({ suppliers, onAdded, onUpdated, onDeleted }) {
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({ name: "", email: "", phone: "", website: "", contact_person: "", address: "", notes: "" });

    const create = async () => {
        if (!draft.name.trim()) { toast.error("Naam is verplicht"); return; }
        setCreating(true);
        try {
            const { data } = await api.post("/admin/suppliers", draft);
            onAdded(data);
            setDraft({ name: "", email: "", phone: "", website: "", contact_person: "", address: "", notes: "" });
            toast.success(`${data.name} toegevoegd`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div data-testid="admin-suppliers" className="mt-6 space-y-6">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-rose-600">★ Adresboek leveranciers</p>
                <p className="font-display text-2xl mt-1">Contactgegevens leveranciers.</p>
                <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
                    Beheer hier alle contactgegevens van je leveranciers: email, telefoon, website en notities.
                    Handig om snel contact te leggen voor nabestellingen, klachten of offertes.
                </p>
            </div>

            <div className="border border-zinc-200 bg-white">
                <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
                    <p className="label-tiny">★ Nieuwe leverancier toevoegen</p>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SupplierField label="Bedrijfsnaam *" testId="sup-new-name" value={draft.name} onChange={(v) => setDraft(d => ({ ...d, name: v }))} placeholder="bv. Creaplot" />
                    <SupplierField label="Contactpersoon" testId="sup-new-contact" value={draft.contact_person} onChange={(v) => setDraft(d => ({ ...d, contact_person: v }))} placeholder="bv. Jan Janssen" />
                    <SupplierField label="Email" testId="sup-new-email" value={draft.email} onChange={(v) => setDraft(d => ({ ...d, email: v }))} placeholder="info@..." type="email" />
                    <SupplierField label="Telefoon" testId="sup-new-phone" value={draft.phone} onChange={(v) => setDraft(d => ({ ...d, phone: v }))} placeholder="+32 ..." />
                    <SupplierField label="Website" testId="sup-new-website" value={draft.website} onChange={(v) => setDraft(d => ({ ...d, website: v }))} placeholder="https://..." />
                    <SupplierField label="Adres" testId="sup-new-address" value={draft.address} onChange={(v) => setDraft(d => ({ ...d, address: v }))} placeholder="Straat, postcode, gemeente" />
                    <div className="md:col-span-2">
                        <p className="label-tiny mb-1.5">Notities</p>
                        <textarea
                            data-testid="sup-new-notes"
                            value={draft.notes}
                            onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
                            rows={3}
                            className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 resize-y"
                            placeholder="Specialiteit, levertijden, kortingen, account-info, …"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <button
                            data-testid="sup-new-submit"
                            onClick={create} disabled={creating}
                            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                            {creating ? "Toevoegen…" : "+ Leverancier toevoegen"}
                        </button>
                    </div>
                </div>
            </div>

            {(!suppliers || suppliers.length === 0) ? (
                <div data-testid="admin-suppliers-empty" className="border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
                    Nog geen leveranciers. Voeg je eerste hierboven toe.
                </div>
            ) : (
                <div className="space-y-3">
                    {suppliers.map(s => (
                        <SupplierCard key={s.id} supplier={s} onUpdated={onUpdated} onDeleted={onDeleted} />
                    ))}
                </div>
            )}
        </div>
    );
}

function SupplierField({ label, value, onChange, placeholder, type = "text", testId }) {
    return (
        <div>
            <p className="label-tiny mb-1.5">{label}</p>
            <input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
            />
        </div>
    );
}

function SupplierCard({ supplier, onUpdated, onDeleted }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({ ...supplier });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/suppliers/${supplier.id}`, {
                name: draft.name,
                email: draft.email,
                phone: draft.phone,
                website: draft.website,
                contact_person: draft.contact_person,
                address: draft.address,
                notes: draft.notes,
            });
            onUpdated(data);
            setEditing(false);
            toast.success(`${data.name} bijgewerkt`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Verwijder leverancier "${supplier.name}"?`)) return;
        try {
            await api.delete(`/admin/suppliers/${supplier.id}`);
            onDeleted(supplier.id);
            toast.success(`${supplier.name} verwijderd`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    if (editing) {
        return (
            <div data-testid={`sup-card-${supplier.id}`} className="border border-zinc-950 bg-zinc-50 p-5 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SupplierField label="Bedrijfsnaam *" testId={`sup-edit-name-${supplier.id}`} value={draft.name} onChange={(v) => setDraft(d => ({ ...d, name: v }))} />
                    <SupplierField label="Contactpersoon" testId={`sup-edit-contact-${supplier.id}`} value={draft.contact_person || ""} onChange={(v) => setDraft(d => ({ ...d, contact_person: v }))} />
                    <SupplierField label="Email" testId={`sup-edit-email-${supplier.id}`} value={draft.email || ""} onChange={(v) => setDraft(d => ({ ...d, email: v }))} type="email" />
                    <SupplierField label="Telefoon" testId={`sup-edit-phone-${supplier.id}`} value={draft.phone || ""} onChange={(v) => setDraft(d => ({ ...d, phone: v }))} />
                    <SupplierField label="Website" testId={`sup-edit-website-${supplier.id}`} value={draft.website || ""} onChange={(v) => setDraft(d => ({ ...d, website: v }))} />
                    <SupplierField label="Adres" testId={`sup-edit-address-${supplier.id}`} value={draft.address || ""} onChange={(v) => setDraft(d => ({ ...d, address: v }))} />
                </div>
                <div>
                    <p className="label-tiny mb-1.5">Notities</p>
                    <textarea
                        data-testid={`sup-edit-notes-${supplier.id}`}
                        value={draft.notes || ""}
                        onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 resize-y"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`sup-save-${supplier.id}`}
                        onClick={save} disabled={saving}
                        className="text-[10px] px-4 py-2 bg-zinc-950 text-white font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-60">
                        {saving ? "…" : "Opslaan"}
                    </button>
                    <button
                        data-testid={`sup-cancel-${supplier.id}`}
                        onClick={() => { setDraft({ ...supplier }); setEditing(false); }}
                        className="text-[10px] px-4 py-2 border border-zinc-300 hover:border-zinc-950 font-bold uppercase tracking-widest">
                        Annuleer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid={`sup-card-${supplier.id}`} className="border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                    <p className="font-display text-2xl">{supplier.name}</p>
                    {supplier.contact_person && (
                        <p className="text-xs text-zinc-500 mt-0.5">Contact: {supplier.contact_person}</p>
                    )}
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        {supplier.email && (
                            <p>
                                <span className="label-tiny mr-1.5">Email</span>
                                <a href={`mailto:${supplier.email}`} className="text-zinc-700 hover:text-zinc-950 underline decoration-zinc-300">{supplier.email}</a>
                            </p>
                        )}
                        {supplier.phone && (
                            <p>
                                <span className="label-tiny mr-1.5">Tel</span>
                                <a href={`tel:${supplier.phone}`} className="text-zinc-700 hover:text-zinc-950 underline decoration-zinc-300 font-mono-dm">{supplier.phone}</a>
                            </p>
                        )}
                        {supplier.website && (
                            <p>
                                <span className="label-tiny mr-1.5">Web</span>
                                <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-zinc-700 hover:text-zinc-950 underline decoration-zinc-300">{supplier.website.replace(/^https?:\/\//, "")}</a>
                            </p>
                        )}
                        {supplier.address && (
                            <p>
                                <span className="label-tiny mr-1.5">Adres</span>
                                <span className="text-zinc-700">{supplier.address}</span>
                            </p>
                        )}
                    </div>
                    {supplier.notes && (
                        <div className="mt-3 pt-3 border-t border-zinc-100">
                            <p className="label-tiny mb-1">Notities</p>
                            <p className="text-xs text-zinc-600 whitespace-pre-line">{supplier.notes}</p>
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                        data-testid={`sup-edit-${supplier.id}`}
                        onClick={() => setEditing(true)}
                        className="text-[10px] px-4 py-2 border border-zinc-300 hover:border-zinc-950 font-bold uppercase tracking-widest">
                        Wijzig
                    </button>
                    <button
                        data-testid={`sup-delete-${supplier.id}`}
                        onClick={remove}
                        className="text-[10px] px-4 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 hover:border-rose-600 font-bold uppercase tracking-widest">
                        Verwijder
                    </button>
                </div>
            </div>
        </div>
    );
}



// ============================================================================
// BUNDLES ADMIN (Fase 1)
// ============================================================================
function BundlesAdmin({ bundles, onAdded, onUpdated, onDeleted }) {
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({
        name: "", slug: "", description: "", image_url: "",
        base_price: 0, discount_pct: 10, final_price: 0,
        active: true, sort_order: 0, items: [],
    });

    const computeFinal = (base, pct) => Math.max(0, parseFloat(base || 0) * (1 - parseFloat(pct || 0) / 100));

    const create = async () => {
        if (!draft.name.trim() || !draft.slug.trim()) { toast.error("Naam en slug zijn verplicht"); return; }
        setCreating(true);
        try {
            const payload = { ...draft, final_price: computeFinal(draft.base_price, draft.discount_pct) };
            const { data } = await api.post("/admin/bundles", payload);
            onAdded(data);
            setDraft({ name: "", slug: "", description: "", image_url: "", base_price: 0, discount_pct: 10, final_price: 0, active: true, sort_order: 0, items: [] });
            toast.success(`${data.name} toegevoegd`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div data-testid="admin-bundles" className="mt-6 space-y-5">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-rose-600">★ Sticker-pakketten</p>
                <p className="font-display text-2xl mt-1">Maak voordeel-pakketten</p>
                <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
                    Combineer meerdere stickers in een pakket met staffelkorting (-10% / -15% / -20%).
                    Verhoogt gemiddelde orderwaarde aanzienlijk.
                </p>
            </div>

            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny mb-3">★ Nieuw pakket toevoegen</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SupplierField label="Naam *" testId="bundle-new-name" value={draft.name}
                        onChange={(v) => setDraft(d => ({ ...d, name: v, slug: d.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "-") }))}
                        placeholder="bv. Auto-fan pack" />
                    <SupplierField label="Slug *" testId="bundle-new-slug" value={draft.slug}
                        onChange={(v) => setDraft(d => ({ ...d, slug: v.toLowerCase().replace(/[^a-z0-9-]+/g, "-") }))}
                        placeholder="auto-fan-pack" />
                    <SupplierField label="Image URL" testId="bundle-new-image" value={draft.image_url}
                        onChange={(v) => setDraft(d => ({ ...d, image_url: v }))}
                        placeholder="https://..." />
                    <div>
                        <p className="label-tiny mb-1.5">Basisprijs (€)</p>
                        <input type="number" step="0.01" data-testid="bundle-new-base"
                            value={draft.base_price}
                            onChange={(e) => setDraft(d => ({ ...d, base_price: e.target.value }))}
                            className="w-full px-2.5 py-2 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950" />
                    </div>
                    <div>
                        <p className="label-tiny mb-1.5">Korting (%)</p>
                        <input type="number" step="1" min="0" max="80" data-testid="bundle-new-discount"
                            value={draft.discount_pct}
                            onChange={(e) => setDraft(d => ({ ...d, discount_pct: e.target.value }))}
                            className="w-full px-2.5 py-2 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950" />
                    </div>
                    <div>
                        <p className="label-tiny mb-1.5">Eindprijs voorbeeld</p>
                        <p className="px-2.5 py-2 bg-zinc-50 border border-zinc-200 font-display text-lg" data-testid="bundle-new-final">
                            € {computeFinal(draft.base_price, draft.discount_pct).toFixed(2)}
                        </p>
                    </div>
                    <div className="md:col-span-2">
                        <p className="label-tiny mb-1.5">Beschrijving</p>
                        <textarea data-testid="bundle-new-description"
                            value={draft.description}
                            onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
                            rows={2}
                            className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 resize-y"
                            placeholder="5 auto-iconen in 1 voordeel-pakket. Bespaar 10%." />
                    </div>
                    <div className="md:col-span-2">
                        <button data-testid="bundle-new-submit" onClick={create} disabled={creating}
                            className="btn-primary disabled:opacity-60">
                            {creating ? "Bezig…" : "+ Pakket toevoegen"}
                        </button>
                    </div>
                </div>
            </div>

            {(!bundles || bundles.length === 0) ? (
                <div className="border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
                    Geen pakketten. Voeg er hierboven 1 toe.
                </div>
            ) : (
                <div className="space-y-2">
                    {bundles.map(b => (
                        <BundleRow key={b.id} bundle={b} onUpdated={onUpdated} onDeleted={onDeleted} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BundleRow({ bundle, onUpdated, onDeleted }) {
    const [edit, setEdit] = useState({ ...bundle });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const final = Math.max(0, parseFloat(edit.base_price || 0) * (1 - parseFloat(edit.discount_pct || 0) / 100));
            const { data } = await api.patch(`/admin/bundles/${bundle.id}`, {
                name: edit.name, description: edit.description, image_url: edit.image_url,
                base_price: parseFloat(edit.base_price),
                discount_pct: parseFloat(edit.discount_pct),
                final_price: final,
                active: edit.active,
                sort_order: parseInt(edit.sort_order) || 0,
            });
            onUpdated(data);
            toast.success(`${data.name} bijgewerkt`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };
    const remove = async () => {
        if (!window.confirm(`Verwijder "${bundle.name}"?`)) return;
        try {
            await api.delete(`/admin/bundles/${bundle.id}`);
            onDeleted(bundle.id);
            toast.success("Verwijderd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
        }
    };
    const final = (parseFloat(edit.base_price || 0) * (1 - parseFloat(edit.discount_pct || 0) / 100)).toFixed(2);
    return (
        <div className="border border-zinc-200 bg-white p-4 grid grid-cols-1 md:grid-cols-[1fr_100px_80px_100px_80px_140px] gap-3 items-center">
            <input value={edit.name} onChange={(e) => setEdit(d => ({ ...d, name: e.target.value }))}
                className="px-2 py-1.5 border border-zinc-300 text-sm font-semibold focus:outline-none focus:border-zinc-950" />
            <input type="number" step="0.01" value={edit.base_price}
                onChange={(e) => setEdit(d => ({ ...d, base_price: e.target.value }))}
                className="px-2 py-1.5 border border-zinc-300 text-sm font-mono-dm focus:outline-none focus:border-zinc-950" />
            <input type="number" step="1" value={edit.discount_pct}
                onChange={(e) => setEdit(d => ({ ...d, discount_pct: e.target.value }))}
                className="px-2 py-1.5 border border-zinc-300 text-sm font-mono-dm focus:outline-none focus:border-zinc-950" />
            <p className="px-2 py-1.5 bg-emerald-50 border border-emerald-200 text-sm font-mono-dm">€ {final}</p>
            <label className="inline-flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={edit.active} onChange={(e) => setEdit(d => ({ ...d, active: e.target.checked }))} className="w-4 h-4 accent-zinc-950" />
                Actief
            </label>
            <div className="flex gap-1">
                <button onClick={save} disabled={saving}
                    className="text-[10px] px-3 py-1.5 bg-zinc-950 text-white font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-60">
                    {saving ? "…" : "Opslaan"}
                </button>
                <button onClick={remove}
                    className="text-[10px] px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold uppercase tracking-widest">
                    Verw.
                </button>
            </div>
        </div>
    );
}


// ============================================================================
// GIFT CARDS ADMIN (Fase 1)
// ============================================================================
function GiftCardsAdmin({ cards, onUpdated }) {
    const disable = async (code) => {
        if (!window.confirm(`Cadeaubon ${code} deactiveren?`)) return;
        try {
            await api.patch(`/admin/gift-cards/${encodeURIComponent(code)}/disable`);
            onUpdated({ code, status: "disabled" });
            toast.success("Gedeactiveerd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mislukt");
        }
    };

    return (
        <div data-testid="admin-giftcards" className="mt-6 space-y-3">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-rose-600">★ Cadeaubonnen overzicht</p>
                <p className="font-display text-2xl mt-1">Verkochte cadeaubonnen</p>
                <p className="text-sm text-zinc-600 mt-1">
                    Alle verkochte cadeaubon-codes met saldo, vervaldatum en status.
                </p>
            </div>

            {(!cards || cards.length === 0) ? (
                <div className="border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
                    Nog geen cadeaubonnen verkocht.
                </div>
            ) : (
                <div className="border border-zinc-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-500">
                                <th className="p-3">Code</th>
                                <th className="p-3">Bedrag</th>
                                <th className="p-3">Saldo</th>
                                <th className="p-3">Ontvanger</th>
                                <th className="p-3">Vervalt</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Actie</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cards.map(c => (
                                <tr key={c.code} className="border-b border-zinc-100">
                                    <td className="p-3 font-mono-dm text-xs">{c.code}</td>
                                    <td className="p-3 font-mono-dm">€ {(c.initial_amount || 0).toFixed(2)}</td>
                                    <td className="p-3 font-mono-dm font-bold">€ {(c.balance || 0).toFixed(2)}</td>
                                    <td className="p-3 text-xs">
                                        {c.recipient_name || "—"}
                                        {c.recipient_email && <div className="text-zinc-400">{c.recipient_email}</div>}
                                    </td>
                                    <td className="p-3 text-[10px] font-mono-dm">{(c.valid_until || "").slice(0, 10)}</td>
                                    <td className="p-3">
                                        <span className={`text-[10px] px-2 py-0.5 uppercase tracking-widest ${
                                            c.status === "active" ? "bg-emerald-100 text-emerald-800"
                                            : c.status === "depleted" ? "bg-zinc-200 text-zinc-700"
                                            : "bg-rose-100 text-rose-700"
                                        }`}>{c.status}</span>
                                    </td>
                                    <td className="p-3">
                                        {c.status === "active" && (
                                            <button onClick={() => disable(c.code)}
                                                className="text-[10px] px-2 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold uppercase tracking-widest">
                                                Deactiveer
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}



// ============================================================================
// GIFT PRODUCTS ADMIN — admin plaatst eigen cadeau-producten
// ============================================================================
function GiftProductsAdmin({ products, onAdded, onUpdated, onDeleted }) {
    const [creating, setCreating] = useState(false);
    const empty = {
        name: "", slug: "", short: "", description: "",
        category: "geschenk", price: 0, original_price: "",
        image_url: "", badge: "", in_stock: true, visible: true, sort_order: 0,
        occasions: [], recipients: [],
    };
    const [draft, setDraft] = useState(empty);

    const create = async () => {
        if (!draft.name.trim() || !draft.slug.trim()) { toast.error("Naam en slug zijn verplicht"); return; }
        setCreating(true);
        try {
            const payload = {
                ...draft,
                price: parseFloat(draft.price) || 0,
                original_price: draft.original_price ? parseFloat(draft.original_price) : null,
                sort_order: parseInt(draft.sort_order) || 0,
                occasions: draft.occasions || [],
                recipients: draft.recipients || [],
            };
            const { data } = await api.post("/admin/gift-products", payload);
            onAdded(data);
            setDraft(empty);
            toast.success(`${data.name} toegevoegd`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div data-testid="admin-gift-products" className="mt-6 space-y-5">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-rose-600">★ Cadeau-producten</p>
                <p className="font-display text-2xl mt-1">Eigen cadeau-aanbod</p>
                <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
                    Plaats hier zelf cadeau-producten zoals cadeauboxen, themasets, wenskaarten, of seizoensartikelen.
                    Onafhankelijk van stickers en bundels.
                </p>
            </div>

            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny mb-3">★ Nieuw cadeau-product toevoegen</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SupplierField label="Naam *" testId="gp-new-name" value={draft.name}
                        onChange={(v) => setDraft(d => ({ ...d, name: v, slug: d.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "-") }))}
                        placeholder="bv. Verjaardag cadeaubox" />
                    <SupplierField label="Slug *" testId="gp-new-slug" value={draft.slug}
                        onChange={(v) => setDraft(d => ({ ...d, slug: v.toLowerCase().replace(/[^a-z0-9-]+/g, "-") }))}
                        placeholder="verjaardag-cadeaubox" />
                    <div>
                        <p className="label-tiny mb-1.5">Categorie</p>
                        <select className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
                            value={draft.category}
                            onChange={(e) => setDraft(d => ({ ...d, category: e.target.value }))}
                            data-testid="gp-new-category">
                            <option value="geschenk">Geschenk</option>
                            <option value="box">Cadeaubox</option>
                            <option value="kaart">Wenskaart</option>
                            <option value="set">Themaset</option>
                            <option value="seizoen">Seizoensartikel</option>
                        </select>
                    </div>
                    <div>
                        <p className="label-tiny mb-1.5">Prijs (€) *</p>
                        <input type="number" step="0.01" data-testid="gp-new-price"
                            value={draft.price}
                            onChange={(e) => setDraft(d => ({ ...d, price: e.target.value }))}
                            className="w-full px-2.5 py-2 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950" />
                    </div>
                    <div>
                        <p className="label-tiny mb-1.5">Originele prijs (voor korting) — optioneel</p>
                        <input type="number" step="0.01" data-testid="gp-new-original"
                            value={draft.original_price}
                            onChange={(e) => setDraft(d => ({ ...d, original_price: e.target.value }))}
                            placeholder="bv. 24.95"
                            className="w-full px-2.5 py-2 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950" />
                    </div>
                    <SupplierField label="Badge" testId="gp-new-badge" value={draft.badge}
                        onChange={(v) => setDraft(d => ({ ...d, badge: v }))}
                        placeholder="bv. Nieuw, Bestseller" />
                    <div className="md:col-span-2">
                        <p className="label-tiny mb-1.5">Productfoto</p>
                        <ImageUpload
                            value={draft.image_url}
                            onChange={(v) => setDraft(d => ({ ...d, image_url: v }))}
                            testId="gp-new-image"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <p className="label-tiny mb-1.5">Korte beschrijving (1 zin)</p>
                        <input data-testid="gp-new-short"
                            value={draft.short}
                            onChange={(e) => setDraft(d => ({ ...d, short: e.target.value }))}
                            maxLength={140}
                            className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950" />
                    </div>
                    <div className="md:col-span-2">
                        <p className="label-tiny mb-1.5">Volledige beschrijving</p>
                        <textarea data-testid="gp-new-description"
                            value={draft.description}
                            onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
                            rows={3}
                            className="w-full px-2.5 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 resize-y" />
                    </div>
                    <div className="md:col-span-2 flex gap-4 items-center">
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={draft.in_stock} onChange={(e) => setDraft(d => ({ ...d, in_stock: e.target.checked }))} className="w-4 h-4 accent-zinc-950" />
                            {draft.in_stock ? "Op voorraad" : "Op bestelling"}
                        </label>
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={draft.visible} onChange={(e) => setDraft(d => ({ ...d, visible: e.target.checked }))} className="w-4 h-4 accent-zinc-950" />
                            Toon op site
                        </label>
                    </div>
                    <div className="md:col-span-2 pt-3 border-t border-zinc-200">
                        <p className="label-tiny mb-2 text-rose-600">★ Cadeau-vinder tags</p>
                        <p className="text-[11px] text-zinc-500 mb-2">
                            Tag dit cadeau zodat het opduikt in de Cadeau-vinder en Cadeau-quiz. Hoe meer tags, hoe vaker het wordt voorgesteld.
                        </p>
                        <GiftTagPicker
                            occasions={draft.occasions}
                            recipients={draft.recipients}
                            onChange={({ occasions, recipients }) => setDraft(d => ({ ...d, occasions, recipients }))}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <button data-testid="gp-new-submit" onClick={create} disabled={creating}
                            className="btn-primary disabled:opacity-60">
                            {creating ? "Bezig…" : "+ Cadeau-product toevoegen"}
                        </button>
                    </div>
                </div>
            </div>

            {(!products || products.length === 0) ? (
                <div className="border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
                    Nog geen cadeau-producten. Voeg er hierboven 1 toe.
                </div>
            ) : (
                <div className="space-y-2">
                    {products.map(p => (
                        <GiftProductRow key={p.id} product={p} products={products} onUpdated={onUpdated} onDeleted={onDeleted} />
                    ))}
                </div>
            )}
        </div>
    );
}

function GiftProductRow({ product, products, onUpdated, onDeleted }) {
    const [edit, setEdit] = useState({ ...product, original_price: product.original_price ?? "" });
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/gift-products/${product.id}`, {
                name: edit.name,
                short: edit.short,
                description: edit.description,
                category: edit.category,
                price: parseFloat(edit.price),
                original_price: edit.original_price ? parseFloat(edit.original_price) : null,
                image_url: edit.image_url,
                badge: edit.badge,
                in_stock: edit.in_stock,
                visible: edit.visible,
                sort_order: parseInt(edit.sort_order) || 0,
                stock_count: edit.stock_count === "" || edit.stock_count === null || edit.stock_count === undefined ? null : Math.max(0, parseInt(edit.stock_count) || 0),
                occasions: edit.occasions || [],
                recipients: edit.recipients || [],
            });
            onUpdated(data);
            toast.success(`${data.name} bijgewerkt`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };
    const remove = async () => {
        if (!window.confirm(`Verwijder "${product.name}"?`)) return;
        try {
            await api.delete(`/admin/gift-products/${product.id}`);
            onDeleted(product.id);
            toast.success("Verwijderd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
        }
    };
    return (
        <div className="border border-zinc-200 bg-white">
            <div className="p-4 grid grid-cols-1 md:grid-cols-[40px_60px_1fr_100px_90px_90px_180px] gap-3 items-center">
                <ReorderArrows item={product} items={products || []} endpoint="/admin/gift-products" onUpdated={onUpdated} testId="gp-reorder" />
                <div className="w-14 h-14 border border-zinc-200 bg-zinc-50 overflow-hidden">
                    {edit.image_url && <img src={edit.image_url} alt="" className="w-full h-full object-contain" />}
                </div>
                <input value={edit.name} onChange={(e) => setEdit(d => ({ ...d, name: e.target.value }))}
                    className="px-2 py-1.5 border border-zinc-300 text-sm font-semibold focus:outline-none focus:border-zinc-950" />
                <input type="number" step="0.01" value={edit.price}
                    onChange={(e) => setEdit(d => ({ ...d, price: e.target.value }))}
                    className="px-2 py-1.5 border border-zinc-300 text-sm font-mono-dm focus:outline-none focus:border-zinc-950" />
                <label className="inline-flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={edit.in_stock} onChange={(e) => setEdit(d => ({ ...d, in_stock: e.target.checked }))} className="w-4 h-4 accent-zinc-950" />
                    Voorraad
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={edit.visible} onChange={(e) => setEdit(d => ({ ...d, visible: e.target.checked }))} className="w-4 h-4 accent-zinc-950" />
                    Tonen
                </label>
                <div className="flex gap-1 flex-wrap">
                    <button onClick={() => setExpanded(v => !v)} data-testid={`gp-expand-${product.id}`}
                        className="text-[10px] px-2 py-1.5 border border-zinc-300 hover:bg-zinc-50 font-bold uppercase tracking-widest">
                        {expanded ? "Sluit" : "Meer"}
                    </button>
                    <button onClick={save} disabled={saving}
                        className="text-[10px] px-3 py-1.5 bg-zinc-950 text-white font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-60">
                        {saving ? "…" : "Opslaan"}
                    </button>
                    <button onClick={remove}
                        className="text-[10px] px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold uppercase tracking-widest">
                        Verw.
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-zinc-200 p-4 bg-zinc-50 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`gp-expanded-${product.id}`}>
                    <div className="space-y-3">
                        <div>
                            <label className="label-tiny block mb-1">Productfoto</label>
                            <ImageUpload value={edit.image_url}
                                onChange={(url) => setEdit(d => ({ ...d, image_url: url }))}
                                testId={`gp-image-${product.id}`} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-1">Categorie / tag</label>
                            <input className="input-flat" value={edit.category || ""}
                                onChange={(e) => setEdit(d => ({ ...d, category: e.target.value }))}
                                placeholder="bv. geschenk" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-1">Badge</label>
                            <input className="input-flat" value={edit.badge || ""}
                                onChange={(e) => setEdit(d => ({ ...d, badge: e.target.value }))}
                                placeholder="bv. Nieuw, Bestseller" />
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className="label-tiny block mb-1">Korte beschrijving (1 zin)</label>
                            <input className="input-flat" value={edit.short || ""}
                                onChange={(e) => setEdit(d => ({ ...d, short: e.target.value }))}
                                maxLength={140} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-1">Volledige beschrijving</label>
                            <textarea className="input-flat min-h-[80px]" value={edit.description || ""}
                                onChange={(e) => setEdit(d => ({ ...d, description: e.target.value }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-tiny block mb-1">Was-prijs (€)</label>
                                <input type="number" step="0.01" min="0" className="input-flat"
                                    value={edit.original_price}
                                    onChange={(e) => setEdit(d => ({ ...d, original_price: e.target.value }))}
                                    placeholder="Optioneel" />
                            </div>
                            <div>
                                <label className="label-tiny block mb-1">Volgorde</label>
                                <input type="number" className="input-flat" value={edit.sort_order ?? 0}
                                    onChange={(e) => setEdit(d => ({ ...d, sort_order: parseInt(e.target.value) || 0 }))} />
                            </div>
                        </div>
                        <div className="pt-2 border-t border-zinc-200">
                            <label className="label-tiny block mb-1">Stock teller (leeg = niet tellen)</label>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => setEdit(d => ({ ...d, stock_count: Math.max(0, (parseInt(d.stock_count) || 0) - 1) }))}
                                    className="w-8 h-8 border border-zinc-200 hover:bg-white text-sm font-bold">−</button>
                                <input type="number" min="0" className="border border-zinc-200 px-2 py-1.5 w-24 text-center font-mono-dm bg-white"
                                    value={edit.stock_count ?? ""}
                                    onChange={(e) => setEdit(d => ({ ...d, stock_count: e.target.value === "" ? null : parseInt(e.target.value) || 0 }))}
                                    placeholder="∞" />
                                <button type="button" onClick={() => setEdit(d => ({ ...d, stock_count: (parseInt(d.stock_count) || 0) + 1 }))}
                                    className="w-8 h-8 border border-zinc-200 hover:bg-white text-sm font-bold">+</button>
                                <button type="button" onClick={() => setEdit(d => ({ ...d, stock_count: null }))}
                                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-950 underline ml-2">
                                    Niet tellen
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-2 pt-3 border-t border-zinc-200">
                        <p className="label-tiny mb-2 text-rose-600">★ Cadeau-vinder tags</p>
                        <p className="text-[11px] text-zinc-500 mb-2">
                            Selecteer voor welke gelegenheden en ontvangers dit cadeau geschikt is.
                        </p>
                        <GiftTagPicker
                            occasions={edit.occasions || []}
                            recipients={edit.recipients || []}
                            onChange={({ occasions, recipients }) => setEdit(d => ({ ...d, occasions, recipients }))}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}



// ============================================================================
// SITE REVIEWS ADMIN — beheer van klant-quotes op de homepage
// ============================================================================
function SiteReviewsAdmin({ reviews, onAdded, onUpdated, onDeleted }) {
    const empty = { author: "", location: "", rating: 5, quote: "", product_type: "", verified: true, visible: true, sort_order: 0 };
    const [draft, setDraft] = useState(empty);
    const [creating, setCreating] = useState(false);

    const create = async () => {
        if (!draft.author.trim() || !draft.quote.trim()) {
            toast.error("Naam en quote zijn verplicht"); return;
        }
        setCreating(true);
        try {
            const { data } = await api.post("/admin/site-reviews", {
                ...draft,
                rating: parseInt(draft.rating) || 5,
                sort_order: parseInt(draft.sort_order) || 0,
            });
            onAdded(data);
            setDraft(empty);
            toast.success("Review toegevoegd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally { setCreating(false); }
    };

    return (
        <div data-testid="admin-site-reviews" className="mt-6 space-y-5">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-rose-600">★ Homepage reviews</p>
                <h3 className="font-display text-2xl mt-1">Voeg een review toe</h3>
                <p className="text-xs text-zinc-500 mt-1">Deze quotes verschijnen op de homepage in de reviews-sectie.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <input className="input-flat" placeholder="Auteur (bv. Lotte V.)" value={draft.author}
                        onChange={(e) => setDraft({ ...draft, author: e.target.value })} data-testid="review-draft-author" />
                    <input className="input-flat" placeholder="Locatie (bv. Sint-Niklaas)" value={draft.location}
                        onChange={(e) => setDraft({ ...draft, location: e.target.value })} data-testid="review-draft-location" />
                    <input className="input-flat" placeholder="Product type (bv. Auto-sticker)" value={draft.product_type}
                        onChange={(e) => setDraft({ ...draft, product_type: e.target.value })} />
                    <select className="input-flat" value={draft.rating}
                        onChange={(e) => setDraft({ ...draft, rating: parseInt(e.target.value) })}>
                        {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} ster{r > 1 ? "ren" : ""}</option>)}
                    </select>
                </div>
                <textarea className="input-flat mt-3 min-h-[80px]" placeholder="De quote..." value={draft.quote}
                    onChange={(e) => setDraft({ ...draft, quote: e.target.value })} data-testid="review-draft-quote" />
                <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={draft.verified}
                            onChange={(e) => setDraft({ ...draft, verified: e.target.checked })} />
                        Geverifieerd badge
                    </label>
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={draft.visible}
                            onChange={(e) => setDraft({ ...draft, visible: e.target.checked })} />
                        Zichtbaar op homepage
                    </label>
                    <label className="inline-flex items-center gap-2">
                        Volgorde:
                        <input type="number" className="w-20 border border-zinc-200 px-2 py-1" value={draft.sort_order}
                            onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
                    </label>
                </div>
                <button onClick={create} disabled={creating} data-testid="review-create-btn"
                    className="btn-primary mt-4 inline-flex items-center gap-2">
                    {creating ? "Bezig..." : "Review toevoegen"}
                </button>
            </div>

            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny mb-4">Bestaande reviews · {reviews.length}</p>
                {reviews.length === 0 ? (
                    <p className="text-sm text-zinc-500">Nog geen reviews. Voeg er hierboven eentje toe.</p>
                ) : (
                    <div className="space-y-3">
                        {reviews.map(r => (
                            <SiteReviewRow key={r.id} review={r} onUpdated={onUpdated} onDeleted={onDeleted} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function SiteReviewRow({ review, onUpdated, onDeleted }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(review);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/site-reviews/${review.id}`, {
                author: draft.author, location: draft.location, rating: parseInt(draft.rating) || 5,
                quote: draft.quote, product_type: draft.product_type, verified: draft.verified,
                visible: draft.visible, sort_order: parseInt(draft.sort_order) || 0,
            });
            onUpdated(data);
            setEditing(false);
            toast.success("Opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm(`Verwijder review van ${review.author}?`)) return;
        try {
            await api.delete(`/admin/site-reviews/${review.id}`);
            onDeleted(review.id);
            toast.success("Verwijderd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    if (!editing) {
        return (
            <div className="border border-zinc-200 p-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold">{review.author}</p>
                        {review.location && <span className="text-[10px] uppercase tracking-widest text-zinc-500">{review.location}</span>}
                        <span className="text-xs text-amber-600">{"★".repeat(review.rating)}</span>
                        {!review.visible && <span className="text-[10px] uppercase tracking-widest bg-zinc-200 px-1.5 py-0.5">Verborgen</span>}
                        {review.verified && <span className="text-[10px] uppercase tracking-widest text-emerald-600">✓ Geverifieerd</span>}
                    </div>
                    <p className="text-sm text-zinc-700 mt-2">&ldquo;{review.quote}&rdquo;</p>
                    {review.product_type && <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-2">{review.product_type} · volgorde {review.sort_order}</p>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => setEditing(true)}
                        className="text-[10px] px-3 py-1.5 border border-zinc-200 hover:bg-zinc-50 font-bold uppercase tracking-widest">Bewerk</button>
                    <button onClick={remove}
                        className="text-[10px] px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold uppercase tracking-widest">Verw.</button>
                </div>
            </div>
        );
    }

    return (
        <div className="border border-zinc-950 p-4 space-y-3 bg-zinc-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="input-flat" value={draft.author}
                    onChange={(e) => setDraft({ ...draft, author: e.target.value })} />
                <input className="input-flat" placeholder="Locatie" value={draft.location}
                    onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
                <input className="input-flat" placeholder="Product type" value={draft.product_type}
                    onChange={(e) => setDraft({ ...draft, product_type: e.target.value })} />
                <select className="input-flat" value={draft.rating}
                    onChange={(e) => setDraft({ ...draft, rating: parseInt(e.target.value) })}>
                    {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} ster{r > 1 ? "ren" : ""}</option>)}
                </select>
            </div>
            <textarea className="input-flat min-h-[80px]" value={draft.quote}
                onChange={(e) => setDraft({ ...draft, quote: e.target.value })} />
            <div className="flex flex-wrap items-center gap-4 text-xs">
                <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={draft.verified}
                        onChange={(e) => setDraft({ ...draft, verified: e.target.checked })} />
                    Geverifieerd
                </label>
                <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={draft.visible}
                        onChange={(e) => setDraft({ ...draft, visible: e.target.checked })} />
                    Zichtbaar
                </label>
                <label className="inline-flex items-center gap-2">
                    Volgorde:
                    <input type="number" className="w-20 border border-zinc-200 px-2 py-1" value={draft.sort_order}
                        onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
                </label>
            </div>
            <div className="flex gap-2">
                <button onClick={save} disabled={saving} className="btn-primary text-xs">{saving ? "..." : "Opslaan"}</button>
                <button onClick={() => { setEditing(false); setDraft(review); }} className="btn-ghost text-xs">Annuleer</button>
            </div>
        </div>
    );
}

// ============================================================================
// TILE SETTINGS ADMIN — beheer prijs + welke tegel/tekstkleuren beschikbaar zijn
// ============================================================================
function TileSettingsAdmin() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingPrice, setSavingPrice] = useState(false);
    const [price, setPrice] = useState("");

    useEffect(() => {
        api.get("/admin/custom-tile/settings")
            .then(({ data }) => {
                setSettings(data);
                setPrice(String(data.price));
                setLoading(false);
            })
            .catch(() => { toast.error("Kon tegel-instellingen niet laden"); setLoading(false); });
    }, []);

    const savePrice = async () => {
        const p = parseFloat(price);
        if (isNaN(p) || p < 0) { toast.error("Ongeldige prijs"); return; }
        setSavingPrice(true);
        try {
            const { data } = await api.patch("/admin/custom-tile/settings", { price: p });
            setSettings(s => ({ ...s, price: data.price }));
            toast.success("Prijs opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mislukt");
        } finally { setSavingPrice(false); }
    };

    const updateColors = async (field, list) => {
        try {
            const payload = field === "tile" ? { tile_colors: list } : { text_colors: list };
            const { data } = await api.patch("/admin/custom-tile/settings", payload);
            setSettings(s => ({ ...s, tile_colors: data.tile_colors, text_colors: data.text_colors }));
            toast.success("Kleuren opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mislukt");
        }
    };

    if (loading || !settings) {
        return <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Tegel-instellingen laden...</div>;
    }

    return (
        <div data-testid="admin-tile-settings" className="mt-6 space-y-5">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-emerald-700">★ Tegeltje met eigen tekst — Instellingen</p>
                <h3 className="font-display text-2xl mt-1">Prijs &amp; beschikbare kleuren</h3>
                <p className="text-xs text-zinc-500 mt-1">Stuur klanten alleen kleuren die jij ook écht kan leveren.</p>

                {/* Prijs */}
                <div className="mt-5 flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="label-tiny block mb-1">Prijs per tegeltje (€)</label>
                        <input
                            data-testid="tile-price-input"
                            type="number" step="0.01" min="0"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="border border-zinc-200 px-3 py-2 w-32 font-mono-dm"
                        />
                    </div>
                    <button onClick={savePrice} disabled={savingPrice}
                        data-testid="tile-price-save"
                        className="btn-primary text-xs">
                        {savingPrice ? "..." : "Prijs opslaan"}
                    </button>
                    <span className="text-[11px] text-zinc-500">Huidig: € {settings.price.toFixed(2)}</span>
                </div>
            </div>

            <TileColorEditor
                title="Tegelkleuren (achtergrond van de tegel)"
                hint="Vink uit wat je niet wil aanbieden. Voeg eigen kleuren toe met naam + hex (#FFFFFF)."
                field="tile"
                colors={settings.tile_colors}
                onSave={(list) => updateColors("tile", list)}
            />

            <TileColorEditor
                title="Tekstkleuren (kleur van de letters)"
                hint="Idem voor de tekstkleur — vink uit wat niet beschikbaar is."
                field="text"
                colors={settings.text_colors}
                onSave={(list) => updateColors("text", list)}
            />
        </div>
    );
}

function TileColorEditor({ title, hint, field, colors, onSave }) {
    const [list, setList] = useState(colors);
    const [newName, setNewName] = useState("");
    const [newHex, setNewHex] = useState("#000000");
    const [saving, setSaving] = useState(false);

    useEffect(() => { setList(colors); }, [colors]);

    const toggle = (idx) => {
        const copy = [...list];
        copy[idx] = { ...copy[idx], enabled: !copy[idx].enabled };
        setList(copy);
    };

    const remove = (idx) => {
        if (!window.confirm(`Kleur "${list[idx].name}" verwijderen?`)) return;
        setList(list.filter((_, i) => i !== idx));
    };

    const add = () => {
        if (!newName.trim()) { toast.error("Geef de kleur een naam"); return; }
        if (!/^#[0-9A-Fa-f]{6}$/.test(newHex)) { toast.error("Hex moet #RRGGBB zijn"); return; }
        setList([...list, { name: newName.trim(), hex: newHex.toUpperCase(), enabled: true }]);
        setNewName(""); setNewHex("#000000");
    };

    const save = async () => {
        setSaving(true);
        await onSave(list);
        setSaving(false);
    };

    const enabledCount = list.filter(c => c.enabled).length;

    return (
        <div className="border border-zinc-200 bg-white p-5" data-testid={`tile-${field}-color-editor`}>
            <div className="flex items-end justify-between flex-wrap gap-2">
                <div>
                    <p className="label-tiny text-rose-600">{title}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">{hint}</p>
                </div>
                <span className="text-[11px] font-mono-dm">
                    <strong>{enabledCount}</strong> / {list.length} aan
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-4">
                {list.map((c, idx) => (
                    <div key={`${c.hex}-${idx}`}
                        className={`flex items-center gap-2 p-2 border ${c.enabled ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50 opacity-60"}`}>
                        <div className="w-7 h-7 border border-zinc-300 shrink-0" style={{ background: c.hex }} />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold truncate">{c.name}</p>
                            <p className="text-[10px] font-mono-dm text-zinc-500">{c.hex}</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={!!c.enabled}
                            onChange={() => toggle(idx)}
                            data-testid={`tile-${field}-toggle-${idx}`}
                            title={c.enabled ? "Uitschakelen" : "Inschakelen"}
                        />
                        <button onClick={() => remove(idx)} className="text-rose-600 text-[10px] font-bold uppercase tracking-widest hover:underline" title="Verwijder">×</button>
                    </div>
                ))}
            </div>

            {/* Toevoegen */}
            <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap items-end gap-2">
                <div>
                    <label className="label-tiny block mb-1">Naam</label>
                    <input className="border border-zinc-200 px-2 py-1.5 text-sm w-40"
                        placeholder="Bv. Bordeauxrood"
                        value={newName} onChange={(e) => setNewName(e.target.value)}
                        data-testid={`tile-${field}-new-name`} />
                </div>
                <div>
                    <label className="label-tiny block mb-1">Hex</label>
                    <input className="border border-zinc-200 px-2 py-1.5 text-sm w-24 font-mono-dm"
                        value={newHex} onChange={(e) => setNewHex(e.target.value)}
                        data-testid={`tile-${field}-new-hex`} />
                </div>
                <input type="color" value={newHex} onChange={(e) => setNewHex(e.target.value.toUpperCase())}
                    className="w-10 h-10 border border-zinc-200 cursor-pointer" title="Picker" />
                <button onClick={add} className="btn-ghost text-xs" data-testid={`tile-${field}-add-color`}>+ Toevoegen</button>
                <button onClick={save} disabled={saving}
                    className="btn-primary text-xs ml-auto"
                    data-testid={`tile-${field}-save-colors`}>
                    {saving ? "Opslaan..." : "Wijzigingen opslaan"}
                </button>
            </div>
        </div>
    );
}


// ============================================================================
// TILE TEMPLATES ADMIN — beheer voorbeeld-tegeltjes voor de klant
// ============================================================================
function TileTemplatesAdmin() {
    const [templates, setTemplates] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);   // template object or null

    useEffect(() => {
        Promise.all([
            api.get("/admin/tile-templates"),
            api.get("/admin/custom-tile/settings"),
        ]).then(([tplRes, cfgRes]) => {
            setTemplates(tplRes.data || []);
            setConfig(cfgRes.data);
            setLoading(false);
        }).catch(() => { toast.error("Kon voorbeelden niet laden"); setLoading(false); });
    }, []);

    const onSaved = (tpl) => {
        if (editing) {
            setTemplates(prev => prev.map(t => t.id === tpl.id ? tpl : t));
        } else {
            setTemplates(prev => [...prev, tpl]);
        }
        setShowForm(false);
        setEditing(null);
    };

    const remove = async (tpl) => {
        if (!window.confirm(`Voorbeeld "${tpl.name}" verwijderen?`)) return;
        try {
            await api.delete(`/admin/tile-templates/${tpl.id}`);
            setTemplates(prev => prev.filter(t => t.id !== tpl.id));
            toast.success("Verwijderd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mislukt");
        }
    };

    const toggleVisible = async (tpl) => {
        try {
            const { data } = await api.patch(`/admin/tile-templates/${tpl.id}`, { visible: !tpl.visible });
            setTemplates(prev => prev.map(t => t.id === tpl.id ? data : t));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mislukt");
        }
    };

    if (loading || !config) {
        return <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Voorbeelden laden...</div>;
    }

    return (
        <div data-testid="admin-tile-templates" className="mt-6 space-y-5">
            <div className="border border-zinc-200 bg-white p-5">
                <div className="flex items-end justify-between flex-wrap gap-2">
                    <div>
                        <p className="label-tiny text-emerald-700">★ Tegel — Voorbeelden / Inspiratie</p>
                        <h3 className="font-display text-2xl mt-1">Beheer voorbeeld-tegeltjes</h3>
                        <p className="text-xs text-zinc-500 mt-1">Klanten zien deze in de Tegel Designer en kunnen er één met één klik toepassen.</p>
                    </div>
                    <button
                        onClick={() => { setEditing(null); setShowForm(true); }}
                        className="btn-primary text-xs"
                        data-testid="tile-tpl-new-btn">
                        + Nieuw voorbeeld
                    </button>
                </div>

                {showForm && (
                    <TileTemplateForm
                        config={config}
                        template={editing}
                        onCancel={() => { setShowForm(false); setEditing(null); }}
                        onSaved={onSaved}
                    />
                )}
            </div>

            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny mb-4">Bestaande voorbeelden · {templates.length}</p>
                {templates.length === 0 ? (
                    <p className="text-sm text-zinc-500">Nog geen voorbeelden. Voeg er hierboven eentje toe.</p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {templates.map(tpl => {
                            const tplFontCss = (config.fonts.find(f => f.id === tpl.font) || config.fonts[0]).css;
                            return (
                                <div key={tpl.id} className={`border ${tpl.visible ? "border-zinc-200" : "border-zinc-200 opacity-60"} bg-white overflow-hidden`}>
                                    {tpl.image_url ? (
                                        <div className="aspect-square bg-zinc-100 relative overflow-hidden">
                                            <img
                                                src={tpl.image_url}
                                                alt={tpl.name}
                                                className="absolute inset-0 w-full h-full object-cover"
                                                onError={(e) => { e.target.style.display = "none"; }}
                                            />
                                            <span className="absolute top-2 left-2 bg-white/90 text-zinc-950 text-[9px] font-bold uppercase tracking-widest px-2 py-1">Foto</span>
                                        </div>
                                    ) : (
                                        <div className="aspect-square flex items-center justify-center p-3" style={{ background: tpl.tile_color }}>
                                            <span
                                                className="text-center leading-tight whitespace-pre-line"
                                                style={{
                                                    fontFamily: tplFontCss,
                                                    color: tpl.text_color,
                                                    fontWeight: 700,
                                                    fontSize: `${14 * (tpl.text_scale || 1)}px`,
                                                }}
                                            >
                                                {tpl.text}
                                            </span>
                                        </div>
                                    )}
                                    <div className="p-2 border-t border-zinc-100">
                                        <p className="text-xs font-bold truncate">{tpl.name}</p>
                                        <p className="text-[10px] font-mono-dm text-zinc-500 truncate">
                                            {tpl.tile_color_name} / {tpl.text_color_name} · {tpl.font}
                                        </p>
                                        <p className="text-[10px] font-mono-dm mt-0.5" data-testid={`tpl-card-price-${tpl.id}`}>
                                            {tpl.price != null && tpl.price !== "" ? (
                                                <span className="text-rose-700 font-bold">€ {Number(tpl.price).toFixed(2)}</span>
                                            ) : (
                                                <span className="text-zinc-400 italic">globale prijs</span>
                                            )}
                                        </p>
                                        <div className="mt-2 flex gap-1">
                                            <button onClick={() => { setEditing(tpl); setShowForm(true); }}
                                                data-testid={`tpl-edit-${tpl.id}`}
                                                className="flex-1 text-[10px] px-2 py-1 border border-zinc-200 hover:bg-zinc-50 font-bold uppercase tracking-widest">
                                                Bewerk
                                            </button>
                                            <button onClick={() => toggleVisible(tpl)}
                                                title={tpl.visible ? "Verbergen" : "Tonen"}
                                                className={`text-[10px] px-2 py-1 border font-bold uppercase tracking-widest ${tpl.visible ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-zinc-300 text-zinc-500 hover:bg-zinc-50"}`}>
                                                {tpl.visible ? "Aan" : "Uit"}
                                            </button>
                                            <button onClick={() => remove(tpl)}
                                                className="text-[10px] px-2 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold">×</button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function TileTemplateForm({ config, template, onCancel, onSaved }) {
    const empty = {
        name: "", text: "Welkom\nthuis",
        tile_color: config.tile_colors[0].hex, tile_color_name: config.tile_colors[0].name,
        text_color: config.text_colors[1]?.hex || config.text_colors[0].hex,
        text_color_name: config.text_colors[1]?.name || config.text_colors[0].name,
        font: "playfair", text_scale: 1.0, sort_order: 0, visible: true,
        image_url: "", price: "",
    };
    const [draft, setDraft] = useState(template ? { ...template, image_url: template.image_url || "", price: template.price ?? "" } : empty);
    const [saving, setSaving] = useState(false);

    const fontCss = (config.fonts.find(f => f.id === draft.font) || config.fonts[0]).css;

    const updateTile = (c) => setDraft(d => ({ ...d, tile_color: c.hex, tile_color_name: c.name }));
    const updateText = (c) => setDraft(d => ({ ...d, text_color: c.hex, text_color_name: c.name }));

    const save = async () => {
        if (!draft.name.trim() || !draft.text.trim()) {
            toast.error("Naam en tekst zijn verplicht"); return;
        }
        setSaving(true);
        try {
            const payload = {
                name: draft.name.trim(),
                text: draft.text.trim().slice(0, 200),
                tile_color: draft.tile_color,
                tile_color_name: draft.tile_color_name,
                text_color: draft.text_color,
                text_color_name: draft.text_color_name,
                font: draft.font,
                text_scale: parseFloat(draft.text_scale) || 1.0,
                sort_order: parseInt(draft.sort_order) || 0,
                visible: !!draft.visible,
                image_url: (draft.image_url || "").trim() || null,
                price: draft.price === "" || draft.price === null || draft.price === undefined
                    ? null
                    : Math.max(0, parseFloat(draft.price) || 0),
            };
            const { data } = template
                ? await api.patch(`/admin/tile-templates/${template.id}`, payload)
                : await api.post("/admin/tile-templates", payload);
            onSaved(data);
            toast.success(template ? "Bijgewerkt" : "Voorbeeld toegevoegd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally { setSaving(false); }
    };

    return (
        <div className="mt-5 border border-zinc-950 p-5 bg-zinc-50">
            <p className="label-tiny mb-4">{template ? "Bewerk voorbeeld" : "Nieuw voorbeeld"}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Live preview */}
                <div className="space-y-3">
                    <p className="label-tiny mb-2">Live voorbeeld (tekst)</p>
                    <div className="aspect-square border-2 flex items-center justify-center p-6" style={{ background: draft.tile_color, borderColor: "rgba(0,0,0,0.12)" }}>
                        <span
                            className="text-center leading-tight whitespace-pre-line break-words w-full"
                            style={{ fontFamily: fontCss, color: draft.text_color, fontWeight: 700, fontSize: `${24 * (draft.text_scale || 1)}px` }}
                        >
                            {draft.text || "Tekst..."}
                        </span>
                    </div>

                    {/* Foto uploaden (optioneel) */}
                    <div className="pt-2">
                        <p className="label-tiny mb-2">Foto van het afgewerkte tegeltje (optioneel)</p>
                        <p className="text-[11px] text-zinc-500 mb-2">
                            Sleep of klik om een foto te uploaden. Klanten zien deze foto in de Tegel Designer i.p.v. de tekstpreview.
                        </p>
                        <ImageUpload
                            value={draft.image_url}
                            onChange={(url) => setDraft(d => ({ ...d, image_url: url }))}
                            testId="tpl-form-image"
                        />
                    </div>
                </div>

                {/* Form */}
                <div className="space-y-3">
                    <input className="input-flat" placeholder="Naam (bv. Welkom thuis)" value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="tpl-form-name" />
                    <textarea className="input-flat min-h-[80px]" placeholder="Tekst (gebruik Enter voor nieuwe regel)"
                        value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                        maxLength={200} data-testid="tpl-form-text" />

                    <div>
                        <label className="label-tiny block mb-1">Lettertype</label>
                        <select className="input-flat" value={draft.font}
                            onChange={(e) => setDraft({ ...draft, font: e.target.value })}>
                            {config.fonts.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="label-tiny block mb-1">Tekstgrootte: {Math.round((draft.text_scale || 1) * 100)}%</label>
                        <input type="range" min="0.5" max="2.0" step="0.05" value={draft.text_scale || 1.0}
                            onChange={(e) => setDraft({ ...draft, text_scale: parseFloat(e.target.value) })}
                            className="w-full accent-rose-600" />
                    </div>

                    <div>
                        <label className="label-tiny block mb-1">Tegelkleur: <strong>{draft.tile_color_name}</strong></label>
                        <div className="grid grid-cols-10 gap-1">
                            {config.tile_colors.filter(c => c.enabled !== false).map(c => (
                                <button key={c.hex} type="button" onClick={() => updateTile(c)} title={c.name}
                                    className={`aspect-square border-2 ${draft.tile_color === c.hex ? "border-zinc-950 ring-1 ring-zinc-950" : "border-zinc-200"}`}
                                    style={{ background: c.hex }} />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="label-tiny block mb-1">Tekstkleur: <strong>{draft.text_color_name}</strong></label>
                        <div className="grid grid-cols-8 gap-1">
                            {config.text_colors.filter(c => c.enabled !== false).map(c => (
                                <button key={c.hex} type="button" onClick={() => updateText(c)} title={c.name}
                                    className={`aspect-square border-2 ${draft.text_color === c.hex ? "border-zinc-950 ring-1 ring-zinc-950" : "border-zinc-200"}`}
                                    style={{ background: c.hex }} />
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-4 text-xs flex-wrap">
                        <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={draft.visible}
                                onChange={(e) => setDraft({ ...draft, visible: e.target.checked })} />
                            Zichtbaar
                        </label>
                        <label className="inline-flex items-center gap-2">
                            Volgorde:
                            <input type="number" className="w-16 border border-zinc-200 px-2 py-1"
                                value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
                        </label>
                    </div>

                    {/* Prijs overschrijving (optioneel) */}
                    <div className="pt-3 border-t border-zinc-200">
                        <label className="label-tiny block mb-1">Prijs voor dit tegeltje (€) — optioneel</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                step="0.10"
                                min="0"
                                className="input-flat max-w-[140px]"
                                placeholder="Globale prijs"
                                value={draft.price}
                                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                                data-testid="tpl-form-price"
                            />
                            {draft.price !== "" && draft.price !== null && (
                                <button
                                    type="button"
                                    onClick={() => setDraft({ ...draft, price: "" })}
                                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-950 underline"
                                    data-testid="tpl-form-price-clear">
                                    Gebruik globale prijs
                                </button>
                            )}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1.5">
                            Laat leeg om de standaard tegel-prijs te gebruiken. Vul in om enkel voor dit voorbeeld een eigen prijs te tonen.
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-5 flex gap-2">
                <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="tpl-form-save">
                    {saving ? "Opslaan..." : (template ? "Bijwerken" : "Toevoegen")}
                </button>
                <button onClick={onCancel} className="btn-ghost text-xs">Annuleer</button>
            </div>
        </div>
    );
}


// ============================================================================
// SHOP PRODUCTS ADMIN — vrij producten toevoegen aan de shop
// ============================================================================
function ShopProductsAdmin({ products, onAdded, onUpdated, onDeleted }) {
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const onSaved = (p) => {
        if (editing) onUpdated(p); else onAdded(p);
        setShowForm(false);
        setEditing(null);
    };

    const remove = async (p) => {
        if (!window.confirm(`Product "${p.name}" verwijderen?`)) return;
        try {
            await api.delete(`/admin/shop-products/${p.id}`);
            onDeleted(p.id);
            toast.success("Verwijderd");
        } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
    };

    const toggle = async (p, field) => {
        try {
            const { data } = await api.patch(`/admin/shop-products/${p.id}`, { [field]: !p[field] });
            onUpdated(data);
        } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
    };

    return (
        <div data-testid="admin-shop-products" className="mt-6 space-y-5">
            <div className="border border-zinc-200 bg-white p-5">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <p className="label-tiny text-rose-600">★ Shop — Producten</p>
                        <h3 className="font-display text-2xl mt-1">Vrije producten in de shop</h3>
                        <p className="text-xs text-zinc-500 mt-1">Voeg hier alles toe wat niet in een vaste categorie past — frames, mokken, posters, mokken, sleutelhangers...</p>
                    </div>
                    <button
                        onClick={() => { setEditing(null); setShowForm(true); }}
                        data-testid="shop-product-new-btn"
                        className="btn-primary text-xs">
                        + Nieuw product
                    </button>
                </div>

                {showForm && (
                    <ShopProductForm
                        product={editing}
                        onCancel={() => { setShowForm(false); setEditing(null); }}
                        onSaved={onSaved}
                    />
                )}
            </div>

            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny mb-4">Producten · {products.length}</p>
                {products.length === 0 ? (
                    <p className="text-sm text-zinc-500">Nog geen shop-producten. Voeg je eerste product hierboven toe.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {products.map(p => (
                            <div key={p.id} className={`border ${p.visible ? "border-zinc-200" : "border-zinc-200 opacity-60"} bg-white overflow-hidden`}>
                                <div className="aspect-square bg-zinc-100 relative overflow-hidden">
                                    {p.image_url ? (
                                        <img src={p.image_url} alt={p.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = "none"; }} />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-zinc-400 text-xs">Geen foto</div>
                                    )}
                                    {p.badge && (
                                        <span className="absolute top-2 left-2 bg-rose-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">{p.badge}</span>
                                    )}
                                    {!p.in_stock && (
                                        <span className="absolute top-2 right-2 bg-zinc-950 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">Niet op voorraad</span>
                                    )}
                                </div>
                                <div className="p-3 border-t border-zinc-100">
                                    <p className="font-semibold text-sm truncate">{p.name}</p>
                                    <div className="flex items-baseline gap-2 mt-1">
                                        <p className="font-mono-dm text-sm">€ {p.price.toFixed(2)}</p>
                                        {p.compare_at_price > 0 && (
                                            <p className="font-mono-dm text-[11px] line-through text-zinc-400">€ {p.compare_at_price.toFixed(2)}</p>
                                        )}
                                    </div>
                                    <p className="text-[10px] font-mono-dm text-zinc-500 mt-1 truncate">
                                        {p.category || "—"} · volgorde {p.sort_order}
                                    </p>
                                    <StockAdjuster product={p} endpoint="/admin/shop-products" onUpdated={onUpdated} />
                                    <div className="mt-3 flex gap-1 flex-wrap items-center">
                                        <ReorderArrows item={p} items={products} endpoint="/admin/shop-products" onUpdated={onUpdated} testId="shop-reorder" />
                                        <button onClick={() => { setEditing(p); setShowForm(true); }}
                                            data-testid={`shop-product-edit-${p.id}`}
                                            className="flex-1 text-[10px] px-2 py-1.5 border border-zinc-200 hover:bg-zinc-50 font-bold uppercase tracking-widest">
                                            Bewerk
                                        </button>
                                        <button onClick={() => toggle(p, "visible")}
                                            title={p.visible ? "Verbergen" : "Tonen"}
                                            className={`text-[10px] px-2 py-1.5 border font-bold uppercase tracking-widest ${p.visible ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-zinc-300 text-zinc-500 hover:bg-zinc-50"}`}>
                                            {p.visible ? "Aan" : "Uit"}
                                        </button>
                                        <button onClick={() => toggle(p, "in_stock")}
                                            title="Voorraad togglen"
                                            className={`text-[10px] px-2 py-1.5 border font-bold uppercase tracking-widest ${p.in_stock ? "border-zinc-300 text-zinc-700 hover:bg-zinc-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
                                            {p.in_stock ? "VRD" : "—"}
                                        </button>
                                        <button onClick={() => remove(p)}
                                            data-testid={`shop-product-delete-${p.id}`}
                                            className="text-[10px] px-2 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 font-bold">×</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ShopProductForm({ product, onCancel, onSaved }) {
    const empty = {
        slug: "", name: "", description: "", price: 0, compare_at_price: 0,
        image_url: "", category: "", badge: "",
        in_stock: true, stock_count: "", visible: true, sort_order: 0,
        occasions: [], recipients: [],
    };
    const [draft, setDraft] = useState(product ? { ...product, compare_at_price: product.compare_at_price || 0, stock_count: product.stock_count ?? "", occasions: product.occasions || [], recipients: product.recipients || [] } : empty);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!draft.name.trim()) { toast.error("Naam is verplicht"); return; }
        setSaving(true);
        try {
            const payload = {
                slug: (draft.slug || "").trim(),
                name: draft.name.trim(),
                description: (draft.description || "").trim(),
                price: parseFloat(draft.price) || 0,
                compare_at_price: parseFloat(draft.compare_at_price) || 0,
                image_url: (draft.image_url || "").trim(),
                category: (draft.category || "").trim(),
                badge: (draft.badge || "").trim(),
                in_stock: !!draft.in_stock,
                stock_count: draft.stock_count === "" || draft.stock_count === null ? null : Math.max(0, parseInt(draft.stock_count) || 0),
                visible: !!draft.visible,
                sort_order: parseInt(draft.sort_order) || 0,
                occasions: draft.occasions || [],
                recipients: draft.recipients || [],
            };
            const { data } = product
                ? await api.patch(`/admin/shop-products/${product.id}`, payload)
                : await api.post("/admin/shop-products", payload);
            onSaved(data);
            toast.success(product ? "Bijgewerkt" : "Product toegevoegd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Opslaan mislukt");
        } finally { setSaving(false); }
    };

    return (
        <div className="mt-5 border border-zinc-950 p-5 bg-zinc-50" data-testid="shop-product-form">
            <p className="label-tiny mb-4">{product ? "Bewerk product" : "Nieuw product"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Preview */}
                <div>
                    <p className="label-tiny mb-2">Voorbeeld</p>
                    <div className="border border-zinc-200 bg-white">
                        <div className="aspect-square bg-zinc-100 relative overflow-hidden">
                            {draft.image_url ? (
                                <img src={draft.image_url} alt={draft.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.style.display = "none"; }} />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-400 text-xs">Voer een Foto URL in</div>
                            )}
                            {draft.badge && (
                                <span className="absolute top-2 left-2 bg-rose-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">{draft.badge}</span>
                            )}
                        </div>
                        <div className="p-3 border-t border-zinc-100">
                            <p className="font-semibold text-sm">{draft.name || "Productnaam"}</p>
                            <p className="font-mono-dm text-sm mt-1">€ {(parseFloat(draft.price) || 0).toFixed(2)}</p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="space-y-3">
                    <div>
                        <label className="label-tiny block mb-1">Naam *</label>
                        <input className="input-flat" value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            data-testid="shop-product-name"
                            placeholder="Bv. Foto-mok 'Family'" />
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Beschrijving</label>
                        <textarea className="input-flat min-h-[70px]" value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            placeholder="Korte beschrijving voor in de shop" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-1">Prijs (€) *</label>
                            <input type="number" step="0.01" min="0" className="input-flat" value={draft.price}
                                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                                data-testid="shop-product-price" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-1">Was-prijs (€)</label>
                            <input type="number" step="0.01" min="0" className="input-flat" value={draft.compare_at_price}
                                onChange={(e) => setDraft({ ...draft, compare_at_price: e.target.value })}
                                placeholder="Optioneel" />
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Productfoto</label>
                        <ImageUpload
                            value={draft.image_url}
                            onChange={(url) => setDraft({ ...draft, image_url: url })}
                            testId="shop-product-image"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-1">Categorie / tag</label>
                            <input className="input-flat" value={draft.category}
                                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                                placeholder="Bv. Mok, Frame, Poster" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-1">Badge</label>
                            <input className="input-flat" value={draft.badge}
                                onChange={(e) => setDraft({ ...draft, badge: e.target.value })}
                                placeholder="Bv. Nieuw, Tip, -20%" />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-xs">
                        <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={draft.visible}
                                onChange={(e) => setDraft({ ...draft, visible: e.target.checked })} />
                            Zichtbaar in shop
                        </label>
                        <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={draft.in_stock}
                                onChange={(e) => setDraft({ ...draft, in_stock: e.target.checked })} />
                            Op voorraad
                        </label>
                        <label className="inline-flex items-center gap-2">
                            Volgorde:
                            <input type="number" className="w-16 border border-zinc-200 px-2 py-1"
                                value={draft.sort_order}
                                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
                        </label>
                    </div>

                    <div className="pt-3 border-t border-zinc-200 mt-2">
                        <label className="label-tiny block mb-1">Stock teller (laat leeg = niet tellen, alleen "op voorraad" tonen)</label>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setDraft({ ...draft, stock_count: Math.max(0, (parseInt(draft.stock_count) || 0) - 1) })}
                                className="w-8 h-8 border border-zinc-200 hover:bg-zinc-50 text-sm font-bold">−</button>
                            <input type="number" min="0" className="border border-zinc-200 px-2 py-1.5 w-24 text-center font-mono-dm"
                                value={draft.stock_count}
                                onChange={(e) => setDraft({ ...draft, stock_count: e.target.value })}
                                placeholder="∞"
                                data-testid="shop-product-stock" />
                            <button type="button" onClick={() => setDraft({ ...draft, stock_count: (parseInt(draft.stock_count) || 0) + 1 })}
                                className="w-8 h-8 border border-zinc-200 hover:bg-zinc-50 text-sm font-bold">+</button>
                            <button type="button" onClick={() => setDraft({ ...draft, stock_count: "" })}
                                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-950 underline ml-2">
                                Niet tellen
                            </button>
                            <span className="text-[11px] text-zinc-500 ml-auto">
                                {draft.stock_count === "" || draft.stock_count === null
                                    ? "Toont alleen 'Op voorraad' badge"
                                    : `Klant ziet: "${parseInt(draft.stock_count) || 0} op voorraad"`}
                            </span>
                        </div>
                    </div>

                    {/* Cadeau-vinder tags */}
                    <div className="pt-3 border-t border-zinc-200 mt-2">
                        <p className="label-tiny mb-2 text-rose-600">★ Cadeau-vinder tags</p>
                        <GiftTagPicker
                            occasions={draft.occasions}
                            recipients={draft.recipients}
                            onChange={({ occasions, recipients }) => setDraft({ ...draft, occasions, recipients })}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-5 flex gap-2">
                <button onClick={save} disabled={saving} data-testid="shop-product-save"
                    className="btn-primary text-xs">
                    {saving ? "Opslaan..." : (product ? "Bijwerken" : "Toevoegen")}
                </button>
                <button onClick={onCancel} className="btn-ghost text-xs">Annuleer</button>
            </div>
        </div>
    );
}


// ============================================================================
// STOCK ADJUSTER — inline +/- voorraad-knoppen voor admin productkaarten
// ============================================================================
function StockAdjuster({ product, endpoint, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const isTracked = product.stock_count !== null && product.stock_count !== undefined;

    const update = async (newVal) => {
        setBusy(true);
        try {
            const payload = newVal === null
                ? { stock_count: null }
                : { stock_count: Math.max(0, parseInt(newVal) || 0) };
            // Bij 0 stock_count → automatisch in_stock=false; bij >0 → in_stock=true
            if (newVal !== null) {
                payload.in_stock = (parseInt(newVal) || 0) > 0;
            }
            const { data } = await api.patch(`${endpoint}/${product.id}`, payload);
            onUpdated(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Stock-update mislukt");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`stock-adjuster-${product.id}`}>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Voorraad:</span>
            {isTracked ? (
                <>
                    <button disabled={busy} onClick={() => update((product.stock_count || 0) - 1)}
                        className="w-6 h-6 border border-zinc-200 hover:bg-zinc-50 text-xs font-bold leading-none">−</button>
                    <span className={`px-2 py-0.5 text-xs font-mono-dm font-bold min-w-[2rem] text-center ${
                        product.stock_count === 0 ? "text-rose-600" :
                        product.stock_count <= 3 ? "text-amber-600" : "text-emerald-700"
                    }`}>
                        {product.stock_count}
                    </span>
                    <button disabled={busy} onClick={() => update((product.stock_count || 0) + 1)}
                        className="w-6 h-6 border border-zinc-200 hover:bg-zinc-50 text-xs font-bold leading-none">+</button>
                    <button disabled={busy} onClick={() => update(null)}
                        title="Stop met tellen — toon enkel 'op voorraad'"
                        className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-zinc-950 underline ml-1">∞</button>
                </>
            ) : (
                <button disabled={busy} onClick={() => update(0)}
                    className="text-[9px] uppercase tracking-widest border border-zinc-200 px-2 py-0.5 hover:bg-zinc-50">
                    + tellen
                </button>
            )}
        </div>
    );
}


// ============================================================================
// STICKER PRICING ADMIN — beheer prijzen van de Custom Sticker Designer
// ============================================================================
function StickerPricingAdmin() {
    const [settings, setSettings] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/custom-sticker/settings")
            .then(({ data }) => {
                setSettings(data);
                setDraft({
                    setup_fee: data.setup_fee,
                    price_per_cm2: data.price_per_cm2,
                    min_price: data.min_price,
                    fixed_klein: data.fixed_sizes?.klein?.price ?? 4.95,
                    fixed_medium: data.fixed_sizes?.medium?.price ?? 7.95,
                    fixed_groot: data.fixed_sizes?.groot?.price ?? 14.95,
                });
            })
            .catch(() => toast.error("Kon sticker-instellingen niet laden"));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/custom-sticker/settings", {
                setup_fee: parseFloat(draft.setup_fee) || 0,
                price_per_cm2: parseFloat(draft.price_per_cm2) || 0,
                min_price: parseFloat(draft.min_price) || 0,
                fixed_size_klein_price: parseFloat(draft.fixed_klein) || 0,
                fixed_size_medium_price: parseFloat(draft.fixed_medium) || 0,
                fixed_size_groot_price: parseFloat(draft.fixed_groot) || 0,
            });
            setSettings(data);
            toast.success("Prijzen opgeslagen");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mislukt");
        } finally { setSaving(false); }
    };

    if (!settings || !draft) {
        return <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Sticker-instellingen laden...</div>;
    }

    // Live voorbeeld
    const previewSize = 100;  // mm × mm
    const previewArea = (previewSize * previewSize) / 100.0;
    const previewRaw = (parseFloat(draft.setup_fee) || 0) + previewArea * (parseFloat(draft.price_per_cm2) || 0);
    const previewPrice = Math.max(previewRaw, parseFloat(draft.min_price) || 0);

    return (
        <div data-testid="admin-sticker-pricing" className="mt-6 mb-6 space-y-4">
            <div className="border border-zinc-200 bg-white p-5">
                <p className="label-tiny text-rose-600">★ Custom Sticker Designer — Prijzen</p>
                <h3 className="font-display text-2xl mt-1">Beheer prijzen op maat</h3>
                <p className="text-xs text-zinc-500 mt-1">Wijzig hier de setup-fee, prijs per cm² en minimum-prijs voor stickers die klanten zelf ontwerpen. De live preview helpt je inschatten wat een 10×10 cm sticker kost.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                    <div>
                        <label className="label-tiny block mb-1">Setup-fee (€)</label>
                        <input type="number" step="0.01" min="0" className="input-flat font-mono-dm"
                            value={draft.setup_fee} data-testid="sticker-setup-fee"
                            onChange={(e) => setDraft(d => ({ ...d, setup_fee: e.target.value }))} />
                        <p className="text-[10px] text-zinc-500 mt-1">Vaste basisprijs per sticker</p>
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Prijs per cm² (€)</label>
                        <input type="number" step="0.001" min="0" className="input-flat font-mono-dm"
                            value={draft.price_per_cm2} data-testid="sticker-per-cm2"
                            onChange={(e) => setDraft(d => ({ ...d, price_per_cm2: e.target.value }))} />
                        <p className="text-[10px] text-zinc-500 mt-1">Variabele oppervlakte-toeslag</p>
                    </div>
                    <div>
                        <label className="label-tiny block mb-1">Minimum prijs (€)</label>
                        <input type="number" step="0.01" min="0" className="input-flat font-mono-dm"
                            value={draft.min_price} data-testid="sticker-min-price"
                            onChange={(e) => setDraft(d => ({ ...d, min_price: e.target.value }))} />
                        <p className="text-[10px] text-zinc-500 mt-1">Vloer voor kleine ontwerpen</p>
                    </div>
                </div>

                {/* Voorbeeldberekening */}
                <div className="mt-5 border border-zinc-200 bg-zinc-50 p-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <p className="label-tiny text-emerald-700">Live voorbeeld — 10 × 10 cm</p>
                        <p className="text-[11px] text-zinc-600 mt-1 font-mono-dm">
                            {draft.setup_fee} + {previewArea} cm² × {draft.price_per_cm2} = € {previewRaw.toFixed(2)} → min € {parseFloat(draft.min_price).toFixed(2)}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="label-tiny">Klant betaalt</p>
                        <p className="font-display text-3xl">€ {previewPrice.toFixed(2)}</p>
                    </div>
                </div>

                <div className="mt-6 pt-5 border-t border-zinc-200">
                    <p className="label-tiny mb-3">Vaste standaardmaten</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="border border-zinc-200 p-3 bg-white">
                            <p className="text-xs font-bold uppercase tracking-widest">Klein · 5 × 5 cm</p>
                            <input type="number" step="0.01" min="0" className="input-flat font-mono-dm mt-2"
                                value={draft.fixed_klein} data-testid="sticker-fixed-klein"
                                onChange={(e) => setDraft(d => ({ ...d, fixed_klein: e.target.value }))} />
                        </div>
                        <div className="border border-zinc-200 p-3 bg-white">
                            <p className="text-xs font-bold uppercase tracking-widest">Medium · 10 × 10 cm</p>
                            <input type="number" step="0.01" min="0" className="input-flat font-mono-dm mt-2"
                                value={draft.fixed_medium} data-testid="sticker-fixed-medium"
                                onChange={(e) => setDraft(d => ({ ...d, fixed_medium: e.target.value }))} />
                        </div>
                        <div className="border border-zinc-200 p-3 bg-white">
                            <p className="text-xs font-bold uppercase tracking-widest">Groot · 20 × 20 cm</p>
                            <input type="number" step="0.01" min="0" className="input-flat font-mono-dm mt-2"
                                value={draft.fixed_groot} data-testid="sticker-fixed-groot"
                                onChange={(e) => setDraft(d => ({ ...d, fixed_groot: e.target.value }))} />
                        </div>
                    </div>
                </div>

                <div className="mt-5">
                    <button onClick={save} disabled={saving}
                        data-testid="sticker-pricing-save"
                        className="btn-primary text-xs">
                        {saving ? "Opslaan..." : "Alle prijzen opslaan"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function CustomersAdmin() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("recent"); // recent | name | orders | spent
    const [marketingOnly, setMarketingOnly] = useState(false);

    useEffect(() => {
        api.get("/admin/customers")
            .then(({ data }) => setCustomers(data || []))
            .catch(() => setCustomers([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = customers
        .filter(c => {
            if (marketingOnly && !c.marketing_consent) return false;
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return (c.name || "").toLowerCase().includes(q)
                || (c.email || "").toLowerCase().includes(q)
                || (c.city || "").toLowerCase().includes(q)
                || (c.address || "").toLowerCase().includes(q);
        })
        .sort((a, b) => {
            if (sort === "name") return (a.name || "").localeCompare(b.name || "");
            if (sort === "orders") return (b.orders_count || 0) - (a.orders_count || 0);
            if (sort === "spent") return (b.total_spent || 0) - (a.total_spent || 0);
            return (b.created_at || "").localeCompare(a.created_at || ""); // recent
        });

    const downloadCsv = async () => {
        try {
            const res = await api.get("/admin/customers/export", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `klanten-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Klantenlijst gedownload");
        } catch (err) {
            toast.error("Export mislukt");
        }
    };

    const totalSpent = filtered.reduce((s, c) => s + (c.total_spent || 0), 0);
    const totalOrders = filtered.reduce((s, c) => s + (c.orders_count || 0), 0);

    return (
        <div data-testid="admin-customers-section" className="mt-6">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                    <p className="label-tiny">★ Klantenbestand</p>
                    <h2 className="font-display text-3xl mt-1">Geregistreerde klanten ({customers.length})</h2>
                    <p className="text-xs mt-1" style={{ color: "var(--text-2)" }}>
                        {filtered.length} weergave · {totalOrders} bestellingen · € {totalSpent.toFixed(2)} totaal
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <input
                        data-testid="customers-search"
                        type="text" placeholder="Zoek naam, e-mail, adres, stad..."
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        className="input-flat w-64" />
                    <select data-testid="customers-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input-flat w-auto">
                        <option value="recent">Recent geregistreerd</option>
                        <option value="name">Alfabetisch (naam)</option>
                        <option value="orders">Meeste bestellingen</option>
                        <option value="spent">Meest besteed</option>
                    </select>
                    <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-xl cursor-pointer text-xs" style={{ borderColor: "var(--border)", background: marketingOnly ? "var(--rose-tint)" : "white" }}>
                        <input data-testid="customers-marketing-filter" type="checkbox" checked={marketingOnly} onChange={(e) => setMarketingOnly(e.target.checked)} className="w-4 h-4 accent-[var(--rose-deep)]" />
                        ✓ Marketing toegelaten
                    </label>
                    <button onClick={downloadCsv} data-testid="customers-export-csv" className="btn-primary text-xs">
                        ⬇ Export CSV
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm" style={{ color: "var(--text-2)" }}>Laden...</div>
            ) : filtered.length === 0 ? (
                <div className="py-12 text-center text-sm card bg-white p-8" style={{ color: "var(--text-2)" }}>
                    {search ? "Geen klanten gevonden met die zoekterm" : "Nog geen geregistreerde klanten"}
                </div>
            ) : (
                <div className="card overflow-x-auto bg-white">
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead>
                            <tr className="border-b border-[var(--border)]" style={{ background: "var(--bg-warm)" }}>
                                <th className="text-left px-4 py-3 label-tiny">Naam</th>
                                <th className="text-left px-4 py-3 label-tiny">E-mail</th>
                                <th className="text-left px-4 py-3 label-tiny">Adres</th>
                                <th className="text-left px-4 py-3 label-tiny">Marketing</th>
                                <th className="text-left px-4 py-3 label-tiny">Geregistreerd</th>
                                <th className="text-right px-4 py-3 label-tiny">Best.</th>
                                <th className="text-right px-4 py-3 label-tiny">Besteed</th>
                                <th className="text-left px-4 py-3 label-tiny">Laatste</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => {
                                const addrParts = [c.address, c.postal_code, c.city, c.country].filter(Boolean);
                                return (
                                    <tr key={c.id} className="border-b border-[var(--border-soft)] hover:bg-[var(--bg-warm)] transition-colors" data-testid={`customer-row-${c.id}`}>
                                        <td className="px-4 py-3 font-semibold">{c.name || "—"}</td>
                                        <td className="px-4 py-3">
                                            <a href={`mailto:${c.email}`} className="hover:underline" style={{ color: "var(--rose-deep)" }}>{c.email}</a>
                                            {c.phone && <div className="text-[11px] font-mono-dm" style={{ color: "var(--text-3)" }}>{c.phone}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-xs" style={{ color: "var(--text-2)" }}>
                                            {addrParts.length > 0 ? addrParts.join(", ") : <span style={{ color: "var(--text-3)" }}>—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {c.marketing_consent
                                                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "rgba(107,142,90,0.18)", color: "var(--success)" }}>✓ JA</span>
                                                : <span className="text-[10px]" style={{ color: "var(--text-3)" }}>nee</span>}
                                        </td>
                                        <td className="px-4 py-3 font-mono-dm text-xs">{(c.created_at || "").slice(0, 10)}</td>
                                        <td className="px-4 py-3 text-right font-mono-dm">{c.orders_count || 0}</td>
                                        <td className="px-4 py-3 text-right font-mono-dm">€ {(c.total_spent || 0).toFixed(2)}</td>
                                        <td className="px-4 py-3 text-xs" style={{ color: "var(--text-3)" }}>{(c.last_order_at || "").slice(0, 10) || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

